import type { ReactNode } from "react";
import { createElement as h, Fragment } from "react";

export type Article = {
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  publishedAt: string;
  updatedAt?: string;
  readingMinutes: number;
  keywords: string[];
  content: ReactNode;
};

const bokforaIdeellForening: Article = {
  slug: "bokfora-ideell-forening-steg-for-steg",
  title: "Hur bokför man en ideell förening? Steg för steg 2026",
  description:
    "Ny som kassör? Lär dig bokföra din ideella förening från grunden: bokföringsskyldighet, metod, kontoplan och bokslut. Komplett guide 2026.",
  excerpt:
    "Ny som kassör? Lär dig bokföra din ideella förening från grunden: bokföringsskyldighet, metod, program, kontoplan och bokslut, steg för steg.",
  publishedAt: "2026-02-15",
  updatedAt: "2026-02-15",
  readingMinutes: 8,
  keywords: [
    "bokföring ideell förening",
    "kassör förening",
    "bokföringsprogram förening",
    "kontoplan förening",
    "bokslut ideell förening",
  ],
  content: h(Fragment, null,
    h("p", null, "Att bli kassör i en ideell förening kan kännas tungt, särskilt om du aldrig har bokfört förut. För de flesta mindre föreningar är det enklare än det låter. I den här guiden går vi igenom allt du behöver veta, från grunden och uppåt."),

    h("h2", null, "Måste din förening bokföra?"),
    h("p", null, "Innan vi går in på hur du bokför är det viktigt att förstå om din förening faktiskt är bokföringsskyldig."),
    h("p", null, "En ideell förening är bokföringsskyldig om den uppfyller minst ett av följande villkor:"),
    h("ul", null,
      h("li", null, "Föreningen bedriver näringsverksamhet (t.ex. driver en kiosk eller hyr ut lokaler mot betalning)"),
      h("li", null, "Föreningen har tillgångar som överstiger 1,5 miljoner kronor"),
      h("li", null, "Föreningen är moderföretag i en koncern"),
    ),
    h("p", null, "Uppfyller din förening inget av ovanstående är ni tekniskt sett inte bokföringsskyldiga. Men det är ändå klokt att bokföra. Det underlättar vid ansökan om bidrag, ger styrelsen kontroll över ekonomin och gör överlämningen till nästa kassör mycket smidigare."),

    h("h2", null, "Vad innebär bokföring i praktiken?"),
    h("p", null, "Bokföring handlar om att systematiskt dokumentera alla ekonomiska händelser i föreningen. Varje gång pengar rör sig, in eller ut, ska det noteras och kopplas till ett underlag (ett kvitto, en faktura eller ett kontoutdrag)."),
    h("p", null, h("strong", null, "De vanligaste inkomsterna i en ideell förening är:")),
    h("ul", null,
      h("li", null, "Medlemsavgifter"),
      h("li", null, "Bidrag från kommunen eller förbund"),
      h("li", null, "Intäkter från aktiviteter, loppmarknader eller evenemang"),
      h("li", null, "Sponsring"),
    ),
    h("p", null, h("strong", null, "De vanligaste utgifterna är:")),
    h("ul", null,
      h("li", null, "Lokalkostnader"),
      h("li", null, "Inköp av material och utrustning"),
      h("li", null, "Försäkringar"),
      h("li", null, "Arvoden och löner"),
    ),

    h("h2", null, "Steg för steg: så bokför du din förening"),

    h("h3", null, "Steg 1: Välj bokföringsmetod"),
    h("p", null, "Det finns två huvudsakliga metoder:"),
    h("p", null, h("strong", null, "Kontantmetoden"), " innebär att du bokför när pengarna faktiskt betalas, alltså när de är inne på kontot eller ute ur kassan. Det här är den vanligaste och enklaste metoden för mindre ideella föreningar."),
    h("p", null, h("strong", null, "Fakturametoden (dubbel bokföring)"), " innebär att du bokför när fakturan skickas eller tas emot, oavsett när betalningen sker. Används av föreningar med större omsättning."),
    h("p", null, "För de flesta ideella föreningar med en omsättning under 3 miljoner kronor räcker kontantmetoden utmärkt."),

    h("h3", null, "Steg 2: Skaffa ett bokföringsprogram"),
    h("p", null, "Det är olagligt att bokföra i ett kalkylprogram som Excel. Du måste använda ett program som är avsett för bokföring. Det finns flera alternativ anpassade för föreningar:"),
    h("ul", null,
      h("li", null, h("strong", null, "Fortnox"), ": marknadsledande, används av många föreningar"),
      h("li", null, h("strong", null, "Spiris"), ": specifikt anpassat för ideella föreningar"),
      h("li", null, h("strong", null, "Bokio"), ": enkelt och prisvärt alternativ"),
      h("li", null, h("strong", null, "Kapitas"), ": gratis grundversion, populärt bland studentföreningar"),
    ),
    h("p", null, "Välj ett program som har en kontoplan anpassad för föreningar och som gör det enkelt att lämna över till nästa kassör."),

    h("h3", null, "Steg 3: Sätt upp en kontoplan"),
    h("p", null, "En kontoplan är en lista på bokföringskonton som du använder för att kategorisera alla händelser. De flesta bokföringsprogram för föreningar kommer med en färdig kontoplan baserad på BAS-kontoplanen. Du behöver sällan ändra något. Använd den som den är och anpassa vid behov."),
    h("p", null, h("strong", null, "Vanliga konton för en ideell förening:")),
    h("table", null,
      h("thead", null,
        h("tr", null,
          h("th", null, "Konto"),
          h("th", null, "Beskrivning"),
        ),
      ),
      h("tbody", null,
        h("tr", null, h("td", null, "3010"), h("td", null, "Medlemsavgifter")),
        h("tr", null, h("td", null, "3020"), h("td", null, "Bidrag och gåvor")),
        h("tr", null, h("td", null, "3030"), h("td", null, "Aktivitetsintäkter")),
        h("tr", null, h("td", null, "5010"), h("td", null, "Lokalkostnader")),
        h("tr", null, h("td", null, "6990"), h("td", null, "Övriga kostnader")),
      ),
    ),

    h("h3", null, "Steg 4: Spara alla underlag (verifikationer)"),
    h("p", null, "Varje ekonomisk händelse måste kopplas till ett underlag, en så kallad verifikation. En verifikation kan vara:"),
    h("ul", null,
      h("li", null, "Ett kvitto från en butik"),
      h("li", null, "En faktura från en leverantör"),
      h("li", null, "Ett kontoutdrag från banken"),
      h("li", null, "En utbetalningsavi"),
    ),
    h("p", null, h("strong", null, "Viktigt:"), " Underlagen ska sparas i minst 7 år om föreningen är bokföringsskyldig."),
    h("blockquote", null, h("strong", null, "Tips:"), " Använd ett bokföringsprogram med en mobilapp där du eller andra i styrelsen kan fotografera kvitton direkt. Det sparar mycket tid jämfört med att samla papperskvitton."),

    h("h3", null, "Steg 5: Bokför löpande, inte i efterhand"),
    h("p", null, "Det vanligaste misstaget nybörjarkassörer gör är att vänta med bokföringen till strax före årsmötet. Då har man plötsligt ett år av transaktioner att sortera igenom, och ingen aning om vilket kvitto som hör till vilket köp."),
    h("p", null, "Bokför i stället minst en gång i månaden, helst varje vecka. Det tar 15–30 minuter om du håller det löpande, men kan ta dagar om du väntar för länge."),

    h("h3", null, "Steg 6: Gör ett bokslut vid årets slut"),
    h("p", null, "Vid räkenskapsårets slut, oftast 31 december, stänger du böckerna för det gångna året. Det kallas bokslut. För mindre föreningar med omsättning under 3 miljoner kronor räcker ett förenklat årsbokslut enligt K1-reglerna (BFNAR 2010:1)."),
    h("p", null, h("strong", null, "Bokslutet innehåller:")),
    h("ul", null,
      h("li", null, h("strong", null, "Resultaträkning"), ": årets alla intäkter minus kostnader"),
      h("li", null, h("strong", null, "Balansräkning"), ": föreningens tillgångar och skulder vid årets slut"),
    ),
    h("p", null, "Bokslutet presenteras sedan på årsmötet och granskas av föreningens revisor."),
    h("p", null, "När bokslutet är klart återstår deklarationen till Skatteverket. I vår guide om att ", h("a", { href: "/artiklar/deklarera-ideell-forening-steg-for-steg" }, "deklarera en ideell förening"), " går vi igenom blanketter, datum och när ni kan slippa deklarera helt."),

    h("h2", null, "Vanliga misstag att undvika"),
    h("p", null, h("strong", null, "Att blanda föreningens och egna pengar."), " Använd alltid ett separat bankkonto för föreningen. Betala aldrig föreningskostnader från ditt privata konto om du kan undvika det. Och om du måste, se till att du begär tillbaka pengarna mot ett kvitto."),
    h("p", null, h("strong", null, "Att inte ha rutiner för kvittona."), " Bestäm redan från start vem som är ansvarig för att samla in och lämna in kvitton, och hur ofta det ska ske."),
    h("p", null, h("strong", null, "Att inte ha en attestant."), " Av internkontrollskäl bör en annan person än kassören godkänna (attestera) utlägg och betalningar. I en liten förening kan det vara ordföranden."),
    h("p", null, h("strong", null, "Att ignorera bokföringen tills det är dags för kassörsbyte."), " En ny kassör som tar över kaos är en kassör som snabbt vill avgå."),

    h("h2", null, "Sammanfattning"),
    h("p", null, "Bokföring i en ideell förening behöver inte vara komplicerat. Nyckeln är att:"),
    h("ol", null,
      h("li", null, "Kontrollera om ni är bokföringsskyldiga"),
      h("li", null, "Välja ett enkelt bokföringsprogram"),
      h("li", null, "Bokföra löpande med alla underlag sparade"),
      h("li", null, "Göra ett bokslut vid årets slut"),
    ),
    h("p", null, "Gör du detta konsekvent har du kontroll, och du lämnar en förening som nästa kassör faktiskt vill ta över."),
    h("p", null, h("em", null, "Har du frågor om din förenings ekonomi? Adminlaget hjälper föreningar med administration och bokföring, enkelt och digitalt.")),
  ),
};


const kassorsbyteForening: Article = {
  slug: "kassorsbyte-forening-overlamning",
  title: "Vad händer vid kassörsbyte i förening? Komplett guide 2026",
  description:
    "Allt om kassörsbyte i ideell förening: överlämning, byte av firmatecknare, digitala åtkomster, juridiskt ansvar och en färdig checklista.",
  excerpt:
    "Ska din förening byta kassör? Så genomför ni en trygg överlämning: bokföring, bank, digitala verktyg och en checklista att följa.",
  publishedAt: "2026-03-15",
  updatedAt: "2026-03-15",
  readingMinutes: 7,
  keywords: [
    "kassörsbyte förening",
    "byta kassör ideell förening",
    "överlämning kassör",
    "firmatecknare förening",
    "checklista kassörsbyte",
  ],
  content: h(Fragment, null,
    h("p", null, "Att byta kassör är en av de vanligaste och mest underskattade utmaningarna i en ideell förening. En smidig överlämning tar några timmar och lämnar föreningen i tryggt skick. En dålig överlämning kan skapa problem som dröjer sig kvar i flera år. Den här guiden reder ut vad som faktiskt ska hända, vem som ansvarar för vad och hur du undviker de vanligaste fallgroparna."),

    h("h2", null, "Varför är kassörsbytet så viktigt?"),
    h("p", null, "Till skillnad från många andra roller i en förening hanterar kassören något konkret och känsligt: pengar, avtal, inloggningar och historik. Om överlämningen görs slarvigt riskerar föreningen att:"),
    h("ul", null,
      h("li", null, "Tappa historik över gamla transaktioner och verifikationer"),
      h("li", null, "Förlora åtkomst till bankkonton eller bokföringsprogram"),
      h("li", null, "Stå utan koll på löpande avtal och abonnemang"),
      h("li", null, "Hamna i en situation där ingen vet vad som gjorts, eller varför"),
    ),
    h("p", null, "Det är därför viktigt att kassörsbytet behandlas som ett projekt, inte som ett samtal i korridoren efter ett styrelsemöte."),

    h("h2", null, "När sker ett kassörsbyte?"),
    h("p", null, "Kassörsbyten sker vanligtvis i samband med föreningens årsmöte, då styrelsen väljs om. Det innebär att den avgående kassören bör börja förbereda överlämningen redan en till två månader innan årsmötet, inte dagen efter att den nya kassören valts."),
    h("p", null, "I vissa fall sker byten mitt i verksamhetsåret, till exempel om kassören avgår i förtid på grund av flytt, sjukdom eller annat. Då är en tydlig rutin extra viktig, eftersom bokslut och årsredovisning ännu inte är avslutade."),

    h("h2", null, "Steg för steg: ett korrekt kassörsbyte"),

    h("h3", null, "Steg 1: Avsluta och stäm av bokföringen"),
    h("p", null, "Innan överlämningen ska den avgående kassören se till att bokföringen är à jour. Det innebär att:"),
    h("ul", null,
      h("li", null, "Alla transaktioner fram till överlämningsdatumet är bokförda"),
      h("li", null, "Alla kvitton och underlag är sparade och märkta"),
      h("li", null, "Bankkontot är avstämt mot bokföringen"),
      h("li", null, "Eventuella skulder eller fordringar är noterade"),
    ),
    h("p", null, "Om det finns ett pågående räkenskapsår som inte är avslutat: dokumentera tydligt var i bokföringen man befinner sig, vilka poster som är hanterade och vilka som är öppna."),

    h("h3", null, "Steg 2: Sammanställ ett överlämningsdokument"),
    h("p", null, "Det viktigaste verktyget vid ett kassörsbyte är ett skriftligt överlämningsdokument. Det behöver inte vara avancerat. Ett enkelt dokument med följande information räcker långt:"),
    h("ul", null,
      h("li", null, h("strong", null, "Bankkonton"), ": bank, kontonummer, vem som är behörig firmatecknare"),
      h("li", null, h("strong", null, "Bokföringsprogram"), ": vilket program, inloggningsuppgifter, var backup finns"),
      h("li", null, h("strong", null, "Löpande avtal"), ": hyra, försäkringar, abonnemang, autogiro"),
      h("li", null, h("strong", null, "Pågående bidragsansökningar"), ": vad som sökt, deadlines, kontaktpersoner"),
      h("li", null, h("strong", null, "Obetalda fakturor"), ": vad som ska betalas och när"),
      h("li", null, h("strong", null, "Kontakter"), ": revisor, bank, leverantörer"),
      h("li", null, h("strong", null, "Var verifikationerna förvaras"), ": fysiskt eller digitalt"),
    ),
    h("p", null, "Det här dokumentet är guld värt för den nya kassören och bör uppdateras vid varje byte."),

    h("h3", null, "Steg 3: Byt firmatecknare i banken"),
    h("p", null, "En av de vanligaste misstagen vid kassörsbyte är att glömma att byta firmatecknare. Den avgående kassören ska inte ha kvar behörighet till föreningens bankkonto efter att bytet är genomfört."),
    h("p", null, h("strong", null, "Vad du behöver göra:")),
    h("ul", null,
      h("li", null, "Kontakta föreningens bank"),
      h("li", null, "Ta med ett protokollsutdrag från det styrelsemöte (eller årsmöte) där den nya kassören valdes"),
      h("li", null, "Genomför ägarbytet: den gamla kassören tas bort, den nya läggs till"),
    ),
    h("p", null, "Observera att vissa banker kräver att båda personerna är på plats, eller att det sker via bankens egna digitala tjänster med BankID. Kontrollera bankens rutiner i god tid."),

    h("h3", null, "Steg 4: Överlämna åtkomst till digitala verktyg"),
    h("p", null, "Utöver banken finns ofta ett antal digitala verktyg som kassören hanterar:"),
    h("ul", null,
      h("li", null, "Bokföringsprogram (Fortnox, Spiris, Bokio m.fl.)"),
      h("li", null, "E-postadress kopplad till kassörsrollen"),
      h("li", null, "Delade dokument (Google Drive, Dropbox)"),
      h("li", null, "Eventuella betaltjänster (Swish för företag/förening, kortläsare)"),
    ),
    h("p", null, "Byt lösenord och uppdatera behörigheter. Överlämna inte bara inloggningsuppgifterna utan se till att den gamla kassören inte längre har åtkomst."),

    h("h3", null, "Steg 5: Genomför ett överlämningssamtal"),
    h("p", null, "Avsätt minst ett par timmar för ett fysiskt eller digitalt möte där den avgående kassören går igenom allt med den nya. Gå igenom överlämningsdokumentet punkt för punkt. Den nya kassören ska ställa frågor. Det är nu det är rätt tillfälle, inte tre månader senare när den gamla kassören knappt minns detaljerna."),

    h("h3", null, "Steg 6: Informera revisorn"),
    h("p", null, "Föreningens revisor bör informeras om att ett kassörsbyte har skett. Revisorn kan behöva uppdaterad kontaktinformation och bör veta vem de ska vända sig till inför nästa revision."),

    h("h2", null, "Vad gäller juridiskt?"),
    h("p", null, "Den avgående kassören ansvarar för bokföringen under sin mandatperiod. Det innebär att om fel uppdagas i den gamla bokföringen är det den dåvarande kassörens ansvar, inte den nyas. Det är ytterligare ett skäl att se till att allt är ordentligt dokumenterat och överlämnat."),
    h("p", null, "Verifikationer och räkenskapsinformation ska sparas i minst 7 år om föreningen är bokföringsskyldig. Den nya kassören ansvarar för att detta arkiv bevaras, oavsett om materialet skapades av en föregångare."),

    h("h2", null, "Checklista: kassörsbyte"),
    h("p", null, "Använd den här listan som stöd vid nästa byte:"),
    h("ul", null,
      h("li", null, "Bokföringen är à jour och avstämd"),
      h("li", null, "Alla kvitton och underlag är sparade"),
      h("li", null, "Överlämningsdokument är skrivet och överlämnat"),
      h("li", null, "Firmatecknare är bytt i banken"),
      h("li", null, "Åtkomst till bokföringsprogram är överlämnad"),
      h("li", null, "Övriga digitala behörigheter är uppdaterade"),
      h("li", null, "Överlämningssamtal är genomfört"),
      h("li", null, "Revisorn är informerad"),
    ),

    h("h2", null, "Vanliga misstag vid kassörsbyte"),
    h("p", null, h("strong", null, "Att vänta tills sista minuten."), " Bankärenden tar tid. Börja processen minst fyra veckor innan bytet ska vara klart."),
    h("p", null, h("strong", null, "Att överlämna muntligt."), " \"Jag förklarade allt för dig\" är inte tillräckligt. Skriv ner det."),
    h("p", null, h("strong", null, "Att glömma Swish-kontot."), " Swish för föreningar är kopplat till en specifik person, och det kräver en separat process att byta kontaktperson."),
    h("p", null, h("strong", null, "Att den gamla kassören behåller åtkomst."), " Av säkerhetsskäl bör den avgående kassören inte ha kvar inloggningar efter att bytet är genomfört."),

    h("h2", null, "Sammanfattning"),
    h("p", null, "Ett kassörsbyte behöver inte vara stressigt om man förbereder sig i tid. Det viktigaste är att:"),
    h("ol", null,
      h("li", null, "Se till att bokföringen är à jour innan bytet"),
      h("li", null, "Skriva ett överlämningsdokument"),
      h("li", null, "Byta firmatecknare i banken"),
      h("li", null, "Överlämna alla digitala åtkomster"),
      h("li", null, "Informera revisorn"),
    ),
    h("p", null, "Gör du det här ordentligt lämnar du föreningen i bättre skick än du hittade den, och det är ju hela poängen."),
    h("p", null, h("em", null, "Adminlaget hjälper föreningar att hålla ordning på sin administration, även vid kassörsbyten. Enklare för dig, tryggare för föreningen.")),
  ),
};


const arsredovisningIdeellForening: Article = {
  slug: "maste-ideell-forening-lamna-arsredovisning",
  title: "Måste ideell förening lämna årsredovisning? Guide 2026",
  description:
    "När krävs årsredovisning i en ideell förening? Skillnaden mellan bokslut och årsredovisning, gränsvärden, K1-regler och vad ni faktiskt måste göra.",
  excerpt:
    "Måste er förening lämna årsredovisning? Vi reder ut gränsvärdena, skillnaden mot förenklat årsbokslut och vad ni ska göra vid årets slut.",
  publishedAt: "2026-04-15",
  updatedAt: "2026-04-15",
  readingMinutes: 7,
  keywords: [
    "årsredovisning ideell förening",
    "förenklat årsbokslut",
    "bokslut förening",
    "K1 regelverk",
    "kassör årsmöte",
  ],
  content: h(Fragment, null,
    h("p", null, "Det är en av de vanligaste frågorna bland nyblivna kassörer och styrelser: måste vår förening lämna årsredovisning? Svaret är att det beror på. De flesta mindre ideella föreningar behöver det inte, men det finns undantag. I den här guiden reder vi ut vad som gäller, vad skillnaden är mellan bokslut och årsredovisning och vad din förening faktiskt behöver göra vid årets slut."),

    h("h2", null, "Bokslut och årsredovisning: vad är skillnaden?"),
    h("p", null, "Många blandar ihop begreppen. Här är den enkla förklaringen:"),
    h("p", null, h("strong", null, "Bokslut"), " är ett samlingsbegrepp för att avsluta räkenskapsåret. Det kan göras på två nivåer: förenklat årsbokslut eller fullständigt årsbokslut."),
    h("p", null, h("strong", null, "Årsredovisning"), " är ett mer formellt dokument som följer en striktare mall och i vissa fall måste lämnas in till Bolagsverket. Det är mer omfattande än ett vanligt bokslut."),
    h("p", null, "De flesta mindre ideella föreningar gör ett förenklat årsbokslut, och det räcker."),

    h("h2", null, "Måste din förening lämna årsredovisning?"),
    h("p", null, "En ideell förening är skyldig att upprätta årsredovisning om den uppfyller minst två av följande tre villkor under vart och ett av de två senaste räkenskapsåren:"),
    h("ul", null,
      h("li", null, "Medelantalet anställda har överstigit 50 personer"),
      h("li", null, "Balansomslutningen (totala tillgångar) har överstigit 40 miljoner kronor"),
      h("li", null, "Nettoomsättningen har överstigit 80 miljoner kronor"),
    ),
    h("p", null, "För de allra flesta ideella föreningar, som idrottsklubbar, kulturföreningar, föräldraföreningar, bostadsrättsföreningar och liknande, är svaret nej. Ni är inte skyldiga att upprätta en årsredovisning."),

    h("h2", null, "Vad ska er förening göra i stället?"),
    h("p", null, "Även om ni inte behöver en fullständig årsredovisning ska ni ändå avsluta räkenskapsåret på något sätt. Vilket alternativ som gäller beror på er omsättning:"),

    h("h3", null, "Alternativ 1: Förenklat årsbokslut (de flesta föreningar)"),
    h("p", null, "Gäller föreningar med en total omsättning (inklusive bidrag, gåvor och medlemsavgifter) som normalt inte överstiger 3 miljoner kronor per år."),
    h("p", null, "Det förenklade årsbokslutet följer K1-reglerna (BFNAR 2010:1) och innehåller:"),
    h("ul", null,
      h("li", null, "En resultaträkning: årets intäkter minus kostnader"),
      h("li", null, "En balansräkning: tillgångar och skulder vid årets slut"),
    ),
    h("p", null, "Det här är ett relativt enkelt dokument som de flesta bokföringsprogram genererar automatiskt när du stänger räkenskapsåret."),

    h("h3", null, "Alternativ 2: Fullständigt årsbokslut"),
    h("p", null, "Gäller föreningar som inte uppfyller kraven för förenklat årsbokslut men som ändå inte når upp till gränserna för årsredovisning. Följer K2-regelverket (BFNAR 2017:3)."),

    h("h3", null, "Alternativ 3: Årsredovisning"),
    h("p", null, "Krävs om föreningen uppfyller minst två av de tre gränsvärdena ovan. Årsredovisningen ska i vissa fall registreras hos Bolagsverket och kräver ofta hjälp av en redovisningskonsult."),

    h("h2", null, "Vad ska finnas med i ett förenklat årsbokslut?"),
    h("p", null, "För de flesta ideella föreningar räcker ett förenklat årsbokslut. Det ska innehålla:"),
    h("h3", null, "Resultaträkning"),
    h("p", null, "En sammanställning av alla intäkter och kostnader under året. Exempel:"),
    h("table", null,
      h("thead", null,
        h("tr", null, h("th", null, "Post"), h("th", null, "Belopp")),
      ),
      h("tbody", null,
        h("tr", null, h("td", null, "Medlemsavgifter"), h("td", null, "45 000 kr")),
        h("tr", null, h("td", null, "Bidrag från kommunen"), h("td", null, "30 000 kr")),
        h("tr", null, h("td", null, "Aktivitetsintäkter"), h("td", null, "12 000 kr")),
        h("tr", null, h("td", null, h("strong", null, "Summa intäkter")), h("td", null, h("strong", null, "87 000 kr"))),
        h("tr", null, h("td", null, "Lokalkostnader"), h("td", null, "−24 000 kr")),
        h("tr", null, h("td", null, "Materiel och utrustning"), h("td", null, "−18 000 kr")),
        h("tr", null, h("td", null, "Övriga kostnader"), h("td", null, "−9 000 kr")),
        h("tr", null, h("td", null, h("strong", null, "Årets resultat")), h("td", null, h("strong", null, "36 000 kr"))),
      ),
    ),
    h("h3", null, "Balansräkning"),
    h("p", null, "En ögonblicksbild av föreningens ekonomiska ställning vid årets slut. Visar vad föreningen äger (tillgångar) och vad den är skyldig (skulder)."),

    h("h2", null, "Måste årsredovisningen eller bokslutet lämnas in någonstans?"),
    h("p", null, "För de flesta ideella föreningar är svaret nej. Till skillnad från aktiebolag behöver ni inte lämna in ert bokslut till Bolagsverket eller Skatteverket, såvida ni inte uppfyller kraven för årsredovisning."),
    h("p", null, "Däremot ska bokslutet:"),
    h("ul", null,
      h("li", null, "Presenteras på årsmötet och godkännas av medlemmarna"),
      h("li", null, "Granskas av föreningens revisor innan årsmötet"),
      h("li", null, "Sparas i föreningens arkiv i minst 7 år om föreningen är bokföringsskyldig"),
    ),

    h("h2", null, "Vad händer på årsmötet?"),
    h("p", null, "Kassörens viktigaste uppgift inför årsmötet är att presentera föreningens ekonomi på ett sätt som medlemmarna förstår. Det innebär vanligtvis:"),
    h("ul", null,
      h("li", null, "En muntlig genomgång av årets resultat: gick vi med vinst eller förlust?"),
      h("li", null, "Presentation av resultat- och balansräkning"),
      h("li", null, "Revisorns berättelse: revisorn redogör för sin granskning"),
      h("li", null, "Beslut om ansvarsfrihet för styrelsen"),
    ),
    h("p", null, "Om revisorn anmärker på något i sin berättelse är det viktigt att styrelsen tar det på allvar. Det kan påverka om ni beviljas ansvarsfrihet."),

    h("h2", null, "Måste er förening ha en revisor?"),
    h("p", null, "De flesta ideella föreningar bör ha en revisor, men det är inte alltid ett lagkrav. Kontrollera föreningens stadgar, där brukar det stå om revisorsval är obligatoriskt."),
    h("p", null, "En revisor i en ideell förening behöver inte vara auktoriserad. Det räcker med en person som är oberoende i förhållande till styrelsen och som har tillräckliga kunskaper i redovisning. Undantaget är föreningar som är skyldiga att upprätta årsredovisning. De kan behöva en auktoriserad revisor beroende på storlek."),

    h("h2", null, "Vanliga frågor"),
    h("p", null, h("strong", null, "Måste vi deklarera?"), " Ideella föreningar är i många fall skattebefriade, men det finns undantag. Om föreningen bedriver verksamhet som ses som näringsverksamhet kan ni bli skattepliktiga. Kontakta Skatteverket eller en redovisningskonsult om ni är osäkra."),
    h("p", null, h("strong", null, "Vad händer om vi inte gör ett bokslut?"), " Om föreningen är bokföringsskyldig och inte uppfyller sina skyldigheter kan det i allvarliga fall leda till personligt ansvar för styrelseledamöterna. Det är ytterligare ett skäl att ta bokföringen på allvar."),
    h("p", null, h("strong", null, "Kan vi använda Excel för bokslutet?"), " Nej. Om föreningen är bokföringsskyldig måste ni använda ett godkänt bokföringsprogram. Excel räknas inte som ett sådant."),

    h("h2", null, "Sammanfattning"),
    h("p", null, "De flesta ideella föreningar behöver inte lämna en årsredovisning. Det räcker med ett förenklat årsbokslut om er omsättning understiger 3 miljoner kronor. Det viktigaste är att:"),
    h("ol", null,
      h("li", null, "Kontrollera om er förening är bokföringsskyldig"),
      h("li", null, "Göra ett förenklat årsbokslut om ni är det"),
      h("li", null, "Låta revisorn granska bokslutet innan årsmötet"),
      h("li", null, "Presentera ekonomin för medlemmarna på årsmötet"),
      h("li", null, "Spara alla handlingar i minst 7 år"),
    ),
    h("p", null, "Gör ni det här konsekvent uppfyller ni era skyldigheter, och ni bygger en förening med god ekonomisk ordning."),
    h("p", null, h("em", null, "Adminlaget hjälper ideella föreningar att hålla ordning på ekonomin, från löpande bokföring till bokslut. Enkelt, digitalt och anpassat för föreningslivet.")),
  ),
};

const bokforingsprogramForening: Article = {
  slug: "bokforingsprogram-forening-gratis-betalda",
  title: "Bokföringsprogram för förening: gratis och betalda alternativ 2026",
  description:
    "Jämförelse av bokföringsprogram för ideella föreningar 2026: Kapitas, Spiris, Fortnox, Bokio och SpeedLedger. Gratis och betalda alternativ med för- och nackdelar.",
  excerpt:
    "Vilket bokföringsprogram passar er förening? Vi jämför Kapitas, Spiris, Fortnox, Bokio och SpeedLedger, gratis och betalda alternativ för 2026.",
  publishedAt: "2026-05-15",
  updatedAt: "2026-05-15",
  readingMinutes: 8,
  keywords: [
    "bokföringsprogram förening",
    "gratis bokföringsprogram förening",
    "Kapitas",
    "Spiris",
    "Fortnox förening",
    "Bokio förening",
    "SpeedLedger",
  ],
  content: h(Fragment, null,
    h("p", null, "Att hitta rätt bokföringsprogram för en ideell förening handlar inte om att hitta det dyraste eller mest avancerade. Det handlar om att hitta det som är enkelt nog att användas av en ideell kassör utan ekonomibakgrund, och som klarar av det en förening faktiskt behöver. I den här guiden går vi igenom de bästa alternativen, både gratis och betalda, och hjälper dig välja rätt."),

    h("h2", null, "Vad behöver ett bokföringsprogram för förening klara av?"),
    h("p", null, "Innan vi går igenom alternativen är det värt att tänka igenom vad just er förening behöver. De vanligaste behoven för en ideell förening är:"),
    h("ul", null,
      h("li", null, "Löpande bokföring av inkomster och utgifter"),
      h("li", null, "Hantering av medlemsavgifter"),
      h("li", null, "Enkel fakturering (om ni fakturerar sponsorer eller externa)"),
      h("li", null, "Automatisk koppling till bankkonto (bankkoppling)"),
      h("li", null, "Möjlighet för flera användare: kassör, ordförande, revisor"),
      h("li", null, "Generering av resultat- och balansräkning inför årsmötet"),
      h("li", null, "Enkel överlämning vid kassörsbyte"),
    ),
    h("p", null, "Ju fler av dessa behov ett program uppfyller, desto bättre passar det för föreningsändamål."),

    h("h2", null, "Viktigt att veta om gratis bokföringsprogram"),
    h("p", null, "Det finns ett antal gratis alternativ, men det är viktigt att förstå begränsningarna. Gratis program:"),
    h("ul", null,
      h("li", null, "Har ofta begränsad support, du är hänvisad till forum eller dokumentation"),
      h("li", null, "Kan sakna bankkoppling, vilket innebär mer manuellt arbete"),
      h("li", null, "Uppdateras mer sällan och kan ha färre funktioner"),
      h("li", null, "Passar bäst för mycket små föreningar med få transaktioner"),
    ),
    h("p", null, "Om er förening har många transaktioner, anställda eller söker bidrag regelbundet är ett betalt program ofta värt kostnaden. Det sparar tid som mer än kompenserar för avgiften."),

    h("h2", null, "Gratis bokföringsprogram för föreningar"),
    h("h3", null, "Kapitas"),
    h("p", null, "Kapitas är det mest kända gratis alternativet för ideella föreningar i Sverige. Det är webbaserat och specifikt anpassat för föreningar."),
    h("p", null, h("strong", null, "Passar för:"), " Mindre föreningar med enkel ekonomi, studentsektioner och föreningar med låg omsättning."),
    h("p", null, h("strong", null, "Fördelar:")),
    h("ul", null,
      h("li", null, "Helt gratis i grundversionen"),
      h("li", null, "Anpassad kontoplan för ideella föreningar"),
      h("li", null, "Webbaserat: fungerar i webbläsaren utan installation"),
      h("li", null, "Relativt enkelt att komma igång med"),
    ),
    h("p", null, h("strong", null, "Nackdelar:")),
    h("ul", null,
      h("li", null, "Begränsad support"),
      h("li", null, "Saknar bankkoppling i gratisversionen"),
      h("li", null, "Färre funktioner jämfört med betalda alternativ"),
      h("li", null, "Uppdateras mer sällan"),
    ),

    h("h2", null, "Betalda bokföringsprogram för föreningar"),
    h("h3", null, "Spiris"),
    h("p", null, "Spiris är ett bokföringsprogram byggt specifikt för föreningar och är ett av de mest populära alternativen på den svenska marknaden."),
    h("p", null, h("strong", null, "Pris:"), " Från 179 kr/månad (exkl. moms)"),
    h("p", null, h("strong", null, "Passar för:"), " Ideella föreningar, idrottsföreningar, BRF, samfälligheter och stiftelser av alla storlekar."),
    h("p", null, h("strong", null, "Fördelar:")),
    h("ul", null,
      h("li", null, "Specifikt anpassat för föreningslivet"),
      h("li", null, "Inbyggt medlemsregister med massfakturering"),
      h("li", null, "Mobilapp för kvittofotografering"),
      h("li", null, "Bankkoppling ingår"),
      h("li", null, "Bra support och utbildningsmaterial"),
      h("li", null, "Enkel överlämning vid kassörsbyte"),
    ),
    h("p", null, h("strong", null, "Nackdelar:")),
    h("ul", null,
      h("li", null, "Kostar pengar, vilket kan bli kännbart för en liten förening"),
      h("li", null, "Kan upplevas som mer än vad en liten förening behöver"),
    ),

    h("h3", null, "Fortnox"),
    h("p", null, "Fortnox är marknadsledande inom bokföring i Sverige och används av många föreningar, inte minst för att många redovisningsbyråer arbetar i Fortnox vilket underlättar samarbetet."),
    h("p", null, h("strong", null, "Pris:"), " Från ca 200 kr/månad beroende på paket"),
    h("p", null, h("strong", null, "Passar för:"), " Föreningar som vill ha ett professionellt system med möjlighet att koppla på en redovisningsbyrå."),
    h("p", null, h("strong", null, "Fördelar:")),
    h("ul", null,
      h("li", null, "Marknadsledande med många integrationer"),
      h("li", null, "Bankkoppling och automatisk kontering"),
      h("li", null, "Lätt att bjuda in revisor eller redovisningsbyrå"),
      h("li", null, "Skalbart: växer med föreningen"),
      h("li", null, "Stort ekosystem av tillägg och integrationer"),
    ),
    h("p", null, h("strong", null, "Nackdelar:")),
    h("ul", null,
      h("li", null, "Inte specifikt anpassat för föreningar, kräver viss handpåläggning"),
      h("li", null, "Kan upplevas som komplicerat för en nybörjarkassör"),
      h("li", null, "Priset stiger snabbt med tillägg"),
    ),

    h("h3", null, "Bokio"),
    h("p", null, "Bokio är ett modernt och användarvänligt bokföringsprogram som uppskattas för sin enkelhet. Det är inte specifikt anpassat för föreningar men fungerar utmärkt för många."),
    h("p", null, h("strong", null, "Pris:"), " Gratisnivå finns, betalversioner från ca 149 kr/månad"),
    h("p", null, h("strong", null, "Passar för:"), " Föreningar som vill ha ett enkelt och modernt gränssnitt utan för många funktioner."),
    h("p", null, h("strong", null, "Fördelar:")),
    h("ul", null,
      h("li", null, "Mycket enkelt att komma igång"),
      h("li", null, "Automatisk bokföring via bankkoppling"),
      h("li", null, "Modern design och mobilanpassat"),
      h("li", null, "Gratisnivå för de med färre behov"),
    ),
    h("p", null, h("strong", null, "Nackdelar:")),
    h("ul", null,
      h("li", null, "Saknar föreningsspecifika funktioner som medlemsregister"),
      h("li", null, "Gratisversionen är begränsad"),
      h("li", null, "Inte lika etablerat för föreningsändamål"),
    ),

    h("h3", null, "SpeedLedger"),
    h("p", null, "SpeedLedger är ett webbaserat bokföringsprogram med fokus på enkelhet och automation."),
    h("p", null, h("strong", null, "Pris:"), " Från ca 199 kr/månad"),
    h("p", null, h("strong", null, "Passar för:"), " Föreningar som vill ha automatiserad bokföring med minimal manuell hantering."),
    h("p", null, h("strong", null, "Fördelar:")),
    h("ul", null,
      h("li", null, "Hög grad av automatisering"),
      h("li", null, "Bra bankkoppling"),
      h("li", null, "Tydliga rapporter för styrelsen"),
    ),
    h("p", null, h("strong", null, "Nackdelar:")),
    h("ul", null,
      h("li", null, "Inte specifikt anpassat för föreningar"),
      h("li", null, "Saknar inbyggt medlemsregister"),
    ),

    h("h2", null, "Jämförelsetabell"),
    h("table", null,
      h("thead", null,
        h("tr", null,
          h("th", null, "Program"),
          h("th", null, "Pris"),
          h("th", null, "Föreningsanpassat"),
          h("th", null, "Bankkoppling"),
          h("th", null, "Medlemsregister"),
          h("th", null, "Mobilapp"),
        ),
      ),
      h("tbody", null,
        h("tr", null,
          h("td", null, "Kapitas"),
          h("td", null, "Gratis"),
          h("td", null, "Ja"),
          h("td", null, "Nej"),
          h("td", null, "Nej"),
          h("td", null, "Nej"),
        ),
        h("tr", null,
          h("td", null, "Spiris"),
          h("td", null, "179 kr/mån"),
          h("td", null, "Ja"),
          h("td", null, "Ja"),
          h("td", null, "Ja"),
          h("td", null, "Ja"),
        ),
        h("tr", null,
          h("td", null, "Fortnox"),
          h("td", null, "~200 kr/mån"),
          h("td", null, "Delvis"),
          h("td", null, "Ja"),
          h("td", null, "Nej"),
          h("td", null, "Ja"),
        ),
        h("tr", null,
          h("td", null, "Bokio"),
          h("td", null, "0–149 kr/mån"),
          h("td", null, "Nej"),
          h("td", null, "Ja"),
          h("td", null, "Nej"),
          h("td", null, "Ja"),
        ),
        h("tr", null,
          h("td", null, "SpeedLedger"),
          h("td", null, "~199 kr/mån"),
          h("td", null, "Nej"),
          h("td", null, "Ja"),
          h("td", null, "Nej"),
          h("td", null, "Ja"),
        ),
      ),
    ),

    h("h2", null, "Vilket program ska ni välja?"),
    h("p", null, "Det enkla svaret:"),
    h("ul", null,
      h("li", null, "Mycket liten förening med få transaktioner och noll budget → Kapitas"),
      h("li", null, "Förening som vill ha ett komplett system anpassat för föreningslivet → Spiris"),
      h("li", null, "Förening som samarbetar med en redovisningsbyrå → Fortnox"),
      h("li", null, "Förening som värdesätter enkelhet och modern design → Bokio"),
    ),

    h("h2", null, "Vad kostar det att inte ha ett bokföringsprogram?"),
    h("p", null, "Det är lätt att se kostnaden för ett bokföringsprogram, 150–200 kronor i månaden, och tänka att det inte är värt det. Men det osynliga alternativet har också ett pris:"),
    h("ul", null,
      h("li", null, "Tid som kassören lägger på manuell hantering av kvitton och transaktioner"),
      h("li", null, "Risk för fel i bokföringen som kan orsaka problem vid revision"),
      h("li", null, "Svårigheter vid kassörsbyte när historiken inte finns samlad på ett ställe"),
      h("li", null, "Minskad trovärdighet vid bidragsansökningar om ekonomin inte är ordentligt dokumenterad"),
    ),
    h("p", null, "För de flesta aktiva föreningar med regelbundna transaktioner är ett bokföringsprogram värt varenda krona."),

    h("h2", null, "Checklista: innan du väljer program"),
    h("ul", null,
      h("li", null, "Hur många transaktioner har föreningen per månad?"),
      h("li", null, "Behöver ni ett inbyggt medlemsregister?"),
      h("li", null, "Samarbetar ni med en revisor eller redovisningsbyrå?"),
      h("li", null, "Hur tekniskt bekväm är kassören?"),
      h("li", null, "Hur ofta sker kassörsbyten, och hur viktigt är enkel överlämning?"),
      h("li", null, "Vad är föreningens budget för administration?"),
    ),

    h("h2", null, "Sammanfattning"),
    h("p", null, "Det finns bra bokföringsprogram för föreningar i alla prisklasser. Gratis alternativ som Kapitas räcker för de allra minsta föreningarna, medan betalda alternativ som Spiris och Fortnox erbjuder mer funktionalitet och trygghet för aktiva föreningar. Det viktigaste är att ni väljer ett program ni faktiskt använder. Ett perfekt program som ingen loggar in i är värdelöst."),
    h("p", null, h("em", null, "Adminlaget hjälper föreningar att komma igång med digital administration, oavsett vilket bokföringsprogram ni väljer.")),
  ),
};

const bidragIdrottsforening: Article = {
  slug: "soka-bidrag-idrottsforening-guide",
  title: "Söka bidrag till idrottsförening: komplett guide 2026",
  description:
    "Komplett guide för idrottsföreningar: LOK-stöd, kommunala bidrag, specialförbund, Allmänna arvsfonden och sponsring. Steg för steg, krav, vanliga misstag och checklista.",
  excerpt:
    "Vilka bidrag kan en idrottsförening söka och hur går ni tillväga? Komplett guide till LOK-stöd, kommunala bidrag, specialförbund och mer.",
  publishedAt: "2026-01-15",
  updatedAt: "2026-01-15",
  readingMinutes: 9,
  keywords: [
    "bidrag idrottsförening",
    "LOK-stöd",
    "söka bidrag förening",
    "kommunalt aktivitetsbidrag",
    "Riksidrottsförbundet bidrag",
    "Allmänna arvsfonden",
  ],
  content: h(Fragment, null,
    h("p", null, "Bidrag är för många idrottsföreningar en av de viktigaste inkomstkällorna, men processen kan kännas otydlig och tidskrävande, särskilt för den som aldrig sökt bidrag förut. Den här guiden går igenom vilka bidrag som finns, hur du söker dem och vad du behöver ha på plats för att maximera chansen att få dem beviljade."),

    h("h2", null, "Vilka typer av bidrag finns det?"),
    h("p", null, "Bidrag till idrottsföreningar kommer från flera olika håll. Det är viktigt att känna till alla kanaler. Många föreningar missar pengar de faktiskt har rätt till."),

    h("h3", null, "1. Kommunala aktivitetsbidrag (LOK-stöd via kommunen)"),
    h("p", null, "De flesta kommuner ger bidrag till idrottsföreningar baserat på antalet aktivitetstillfällen för barn och ungdomar. Det kallas ofta aktivitetsbidrag eller LOK-stöd (Lokalt aktivitetsstöd)."),
    h("p", null, "Grundprincipen är enkel: ju fler träningar och aktiviteter ni arrangerar för ungdomar under en viss ålder (vanligtvis 7–25 år), desto mer bidrag får ni. Bidraget söks i efterhand och baseras på rapporterade aktiviteter."),
    h("p", null, h("strong", null, "Viktigt:"), " Varje kommun har sina egna regler, belopp och ansökningsperioder. Kontakta er kommuns idrotts- och fritidsförvaltning för att få exakta uppgifter."),

    h("h3", null, "2. Riksidrottsförbundets LOK-stöd"),
    h("p", null, "Riksidrottsförbundet (RF) fördelar statligt stöd till idrottsföreningar som är anslutna till ett specialförbund. LOK-stödet från RF baseras på samma princip som det kommunala: antalet redovisade aktivitetstillfällen för deltagare i åldern 7–25 år."),
    h("p", null, "För att söka RF:s LOK-stöd måste föreningen:"),
    h("ul", null,
      h("li", null, "Vara ansluten till ett specialförbund inom Riksidrottsförbundet"),
      h("li", null, "Rapportera aktiviteter via RF:s system (Idrottonline)"),
      h("li", null, "Ha minst tre deltagare per aktivitetstillfälle"),
    ),
    h("p", null, "Ansökan sker halvårsvis, en period för januari–juni och en för juli–december."),

    h("h3", null, "3. Specialförbundens egna bidrag"),
    h("p", null, "Utöver RF:s LOK-stöd har de flesta specialförbund (Svenska Fotbollförbundet, Svenska Simförbundet, Svenska Friidrottsförbundet osv.) egna bidragsprogram för sina medlemsföreningar. Det kan handla om:"),
    h("ul", null,
      h("li", null, "Utbildningsbidrag för ledare och tränare"),
      h("li", null, "Bidrag för anläggningar och utrustning"),
      h("li", null, "Bidrag för tävlingsverksamhet"),
      h("li", null, "Ungdomssatsningar och integrationsprojekt"),
    ),
    h("p", null, "Kontakta ert specialförbund direkt för att få veta vilka bidrag som finns tillgängliga."),

    h("h3", null, "4. Regionala och regionförbundets bidrag"),
    h("p", null, "Många regioner och regionförbund fördelar egna medel till föreningslivet, ofta med fokus på folkhälsa, integration eller barn och unga. Dessa bidrag är ofta mindre kända men kan vara värdefulla komplement."),
    h("p", null, "Sök på er regions webbplats efter \"föreningsbidrag\" eller \"idrottsbidrag\" för att se vad som finns tillgängligt."),

    h("h3", null, "5. Allmänna arvsfonden"),
    h("p", null, "Allmänna arvsfonden stödjer projekt som skapar ny verksamhet för barn, ungdomar och personer med funktionsnedsättning. Det är inte ett driftsbidrag utan ett projektbidrag. Ni söker pengar för att starta eller utveckla något nytt."),
    h("p", null, "Bidragen är ofta större (från 100 000 kronor och uppåt) men kräver mer arbete i ansökan och uppföljning. Passa för föreningar som vill starta ett nytt program eller bygga nya faciliteter."),

    h("h3", null, "6. Företagssponsring"),
    h("p", null, "Tekniskt sett inte ett bidrag men fungerar på liknande sätt. Lokala företag kan sponsra föreningen mot exponering på tröjor, banderoller, hemsida eller i sociala medier. Sponsring är momspliktig om föreningen är momsregistrerad, vilket är viktigt att ha koll på."),

    h("h2", null, "Vad krävs för att kunna söka bidrag?"),
    h("p", null, "Oavsett vilken bidragsgivare det handlar om finns det ett antal grundläggande krav som nästan alltid ställs:"),
    h("ol", null,
      h("li", null, h("strong", null, "Registrerad förening med stadgar."), " Föreningen måste vara en ideell förening med antagna stadgar. Vissa bidragsgivare kräver även att föreningen är registrerad hos Skatteverket och har ett organisationsnummer."),
      h("li", null, h("strong", null, "Aktuellt protokoll från senaste årsmötet."), " Bidragsgivare vill se att föreningen är aktiv och har en fungerande styrelse. Ha alltid ett aktuellt styrelseprotokoll tillgängligt."),
      h("li", null, h("strong", null, "Ordnad ekonomi med bokföring."), " De flesta bidragsgivare kräver att ni kan visa upp en resultat- och balansräkning från föregående år. En förening utan ordnad bokföring har svårt att styrka sin ekonomiska situation, och riskerar att nekas bidrag."),
      h("li", null, h("strong", null, "Verksamhetsberättelse."), " En beskrivning av vad föreningen gjort under det gångna året: hur många medlemmar, vilka aktiviteter, vilka resultat. Ju mer konkret och faktabaserad, desto bättre."),
      h("li", null, h("strong", null, "Aktivitetsrapportering (för LOK-stöd)."), " För att söka LOK-stöd måste ni ha löpande rapporterat era aktivitetstillfällen. Det går inte att rapportera i efterhand. Rutinen måste finnas på plats under hela året."),
    ),

    h("h2", null, "Steg för steg: så söker ni bidrag"),
    h("h3", null, "Steg 1: Kartlägg tillgängliga bidrag"),
    h("p", null, "Börja med att göra en lista över alla bidrag er förening kan söka. Kontakta:"),
    h("ul", null,
      h("li", null, "Kommunens idrotts- och fritidsförvaltning"),
      h("li", null, "Ert specialförbund"),
      h("li", null, "Er region"),
      h("li", null, "RF (om ni är anslutna)"),
    ),
    h("p", null, "Fråga specifikt: \"Vilka bidrag kan en förening som vår söka, och när är ansökningsperioderna?\""),

    h("h3", null, "Steg 2: Sätt upp en bidragskalender"),
    h("p", null, "Bidrag har deadlines, och missar ni en ansökningsperiod kan det innebära ett halvår eller ett år utan den intäkten. Skapa en enkel kalender med:"),
    h("ul", null,
      h("li", null, "Vilka bidrag ni söker"),
      h("li", null, "Ansökningsperiod och deadline"),
      h("li", null, "Vem i styrelsen som är ansvarig"),
      h("li", null, "Vilka dokument som krävs"),
    ),

    h("h3", null, "Steg 3: Se till att bokföringen är i ordning"),
    h("p", null, "Innan ni skickar in en ansökan, kontrollera att ni kan bifoga:"),
    h("ul", null,
      h("li", null, "Senaste årets resultat- och balansräkning"),
      h("li", null, "Revisionsberättelse"),
      h("li", null, "Aktuellt organisationsnummer och bankkontonummer"),
    ),
    h("p", null, "En ansökan som saknar dessa handlingar avvisas ofta utan vidare handläggning."),

    h("h3", null, "Steg 4: Skriv en tydlig och konkret ansökan"),
    h("p", null, "En bra bidragsansökan är specifik, inte allmän. Undvik formuleringar som \"vi bedriver ungdomsverksamhet av hög kvalitet.\" Skriv i stället:"),
    h("ul", null,
      h("li", null, "Hur många aktiva medlemmar under 25 år föreningen har"),
      h("li", null, "Hur många träningar per vecka som genomförs"),
      h("li", null, "Vilket specifikt ändamål bidraget ska användas till"),
      h("li", null, "Vad resultatet av bidraget förväntas bli"),
    ),

    h("h3", null, "Steg 5: Följ upp och rapportera"),
    h("p", null, "När ni fått ett bidrag beviljat, glöm inte uppföljningen. Många bidragsgivare kräver en redovisning av hur pengarna använts. Spara kvitton och dokumentera vad bidraget gick till. En förening som redovisar sina bidrag noggrant och i tid stärker sin relation med bidragsgivaren inför nästa ansökningsomgång."),

    h("h2", null, "Vanliga misstag vid bidragsansökan"),
    h("p", null, h("strong", null, "Att missa ansökningsperioden."), " Det vanligaste och enklaste misstaget att undvika. Sätt upp påminnelser i god tid."),
    h("p", null, h("strong", null, "Att inte ha aktiviteterna rapporterade löpande."), " För LOK-stödet gäller att aktiviteter måste rapporteras nära inpå att de genomförs. Gör det till en vana att rapportera varje vecka."),
    h("p", null, h("strong", null, "Att skicka in ofullständiga handlingar."), " Läs ansökningsblanketten noggrant och bifoga allt som efterfrågas. Ring upp handläggaren om ni är osäkra."),
    h("p", null, h("strong", null, "Att inte söka för att man inte tror sig ha chans."), " Många föreningar söker helt enkelt inte de bidrag de har rätt till, antingen av okunnighet eller för att det känns krångligt. Det är pengar som lämnas på bordet."),
    h("p", null, h("strong", null, "Att glömma redovisningen."), " En förening som inte redovisar hur ett beviljat bidrag använts riskerar att nekas framtida bidrag från samma givare."),

    h("h2", null, "Hur mycket kan er förening få?"),
    h("p", null, "Det varierar enormt beroende på kommunen, föreningens storlek och verksamhetens omfattning. Några riktmärken:"),
    h("ul", null,
      h("li", null, h("strong", null, "Kommunalt aktivitetsbidrag:"), " Ofta 20–50 kronor per aktivitetstillfälle och deltagare"),
      h("li", null, h("strong", null, "RF:s LOK-stöd:"), " Cirka 70 kronor per aktivitetstillfälle (beloppet justeras årligen)"),
      h("li", null, h("strong", null, "Specialförbundens bidrag:"), " Varierar kraftigt, från några tusen till hundratusentals kronor för större projekt"),
      h("li", null, h("strong", null, "Allmänna arvsfonden:"), " 100 000 kronor och uppåt för godkända projekt"),
    ),
    h("p", null, "En aktiv ungdomsförening med 50 aktiva ungdomar och tre träningar i veckan kan räkna med LOK-stöd i storleksordningen 50 000–100 000 kronor per år enbart från RF och kommunen."),

    h("h2", null, "Checklista: inför varje bidragsansökan"),
    h("ul", null,
      h("li", null, "Ansökningsperiod och deadline noterad i kalendern"),
      h("li", null, "Senaste årets resultat- och balansräkning finns"),
      h("li", null, "Revisionsberättelse finns"),
      h("li", null, "Aktuellt styrelseprotokoll finns"),
      h("li", null, "Aktiviteter är rapporterade löpande (för LOK-stöd)"),
      h("li", null, "Organisationsnummer och bankkontonummer till hands"),
      h("li", null, "Ansökan är specifik med konkreta siffror och mål"),
      h("li", null, "Ansvarig person i styrelsen utsedd"),
    ),

    h("h2", null, "Sammanfattning"),
    h("p", null, "Bidrag är en central inkomstkälla för de flesta idrottsföreningar, men de kräver förberedelse och rutiner. De viktigaste sakerna att ha på plats är:"),
    h("ul", null,
      h("li", null, "En kartläggning av vilka bidrag er förening kan söka"),
      h("li", null, "En bidragskalender med deadlines"),
      h("li", null, "Ordnad bokföring och uppdaterade handlingar"),
      h("li", null, "Löpande aktivitetsrapportering för LOK-stödet"),
      h("li", null, "Konkreta och välskrivna ansökningar"),
    ),
    h("p", null, "Föreningar som tar bidragsarbetet på allvar och bygger upp goda rutiner kan räkna med ett betydligt stabilare ekonomiskt underlag, och mer tid att lägga på det som faktiskt är roligt: idrotten."),
    h("p", null, h("em", null, "Adminlaget hjälper idrottsföreningar att hålla ordning på ekonomin och administrationen, så att ni kan fokusera på verksamheten.")),
  ),
};

const fritidskortetForening: Article = {
  slug: "fritidskortet-forening-ansluta",
  title: "Fritidskortet för föreningar: så ansluter ni er 2026",
  description:
    "Komplett guide till Fritidskortet 2026: vad det är, vilka föreningar som kan ansluta sig, belopp, ansökan steg för steg och en checklista.",
  excerpt:
    "Fritidskortet ger barn 7–16 år statliga pengar till föreningsavgifter. Så ansluter ni er förening: krav, dokument och ansökan steg för steg.",
  publishedAt: "2026-05-20",
  updatedAt: "2026-05-20",
  readingMinutes: 8,
  keywords: [
    "fritidskortet",
    "fritidskortet förening",
    "ansluta fritidskortet",
    "fritidskortet 2026",
    "försäkringskassan fritidskortet",
  ],
  content: h(Fragment, null,
    h("p", null, "Fritidskortet är en av de viktigaste nyheterna för svenska föreningar på många år. Sedan september 2025 kan barn och ungdomar mellan 7 och 16 år använda statliga pengar för att betala avgifter hos anslutna föreningar. Från januari 2026 gäller stödet för alla barn i den åldern, med höjda belopp. Men för att familjer ska kunna använda kortet hos er måste föreningen vara registrerad. I den här guiden går vi igenom vad Fritidskortet är, om er förening kan ansluta sig och hur ni gör det steg för steg."),

    h("h2", null, "Vad är Fritidskortet?"),
    h("p", null, "Fritidskortet är ett statligt stöd som ger barn och ungdomar i Sverige ekonomisk möjlighet att delta i organiserade fritidsaktiviteter oavsett familjens ekonomi. Det är ett regeringsuppdrag som drivs av Folkhälsomyndigheten, E-hälsomyndigheten, Försäkringskassan, MUCF och Kulturrådet gemensamt. Riksdagen tog beslut om att införa Fritidskortet i juni 2025."),
    h("p", null, "Fritidskortet är till för barn och ungdomar som är mellan 7 och 16 år. Föräldrar och vårdnadshavare kan använda Fritidskortet från det år barnet fyller 7 år till och med det år hen fyller 16 år."),

    h("h2", null, "Hur mycket pengar rör det sig om 2026?"),
    h("p", null, "Från och med januari 2026 gäller följande belopp:"),
    h("ul", null,
      h("li", null, h("strong", null, "Grundbelopp:"), " 550 kronor per barn och år"),
      h("li", null, h("strong", null, "Förhöjt belopp:"), " 2 500 kronor per barn och år för hushåll som fått bostadsbidrag under föregående år"),
    ),
    h("p", null, "Pengarna måste användas senast 30 november varje år och kan inte sparas till nästa år."),

    h("h2", null, "Vad kan pengarna användas till?"),
    h("p", null, "Fritidskortet kan användas till att betala för regelbundna fritidsaktiviteter inom idrott, kultur och friluftsliv. Aktiviteten måste vara ledarledd och regelbunden, minst 6 tillfällen på en sammanhängande period på upp till 6 månader. Även digitala aktiviteter kan ingå."),
    h("p", null, "Avgiftsfria aktiviteter ingår inte. Syftet med Fritidskortet är att ge fler barn och ungdomar möjligheten att delta i fritidsaktiviteter oavsett familjens ekonomi. Aktiviteter som redan är gratis är viktiga men de är redan tillgängliga och omfattas därför inte av stödet."),

    h("h2", null, "Hur stort är Fritidskortet redan?"),
    h("p", null, "Trots att det lanserades så sent som september 2025 fick Fritidskortet ett snabbt genomslag. Redan under de första tre månaderna, fram till sista betalningsdagen 30 november 2025, hade över 111 miljoner kronor gått till barns fritidsaktiviteter och över 200 000 barn hade använt kortet."),
    h("p", null, "Nu när stödet gäller hela kalenderåret 2026 från start väntas användningen öka betydligt. Det är pengar som föreningar i hela Sverige kan ta del av, förutsatt att de är anslutna."),

    h("h2", null, "Kan er förening ansluta sig?"),
    h("p", null, "Det beror på vilken typ av förening ni är. Här är en översikt:"),

    h("h3", null, "Idrottsföreningar"),
    h("p", null, "För att en idrottsförening ska bli ansluten till Fritidskortet krävs att föreningen tagit emot statligt lokalt aktivitetsstöd (LOK-stöd) från Riksidrottsförbundet, samt har ett svenskt organisationsnummer och bankgiro- eller plusgironummer."),
    h("p", null, "Idrottsföreningar som inte får LOK-stöd från RF kan ändå ansluta sig om de är medlem i en av Svenskt Friluftslivs medlemsorganisationer, eller är medlemmar i en riksorganisation som får organisationsbidrag från MUCF."),

    h("h3", null, "Kulturföreningar och kulturskolor"),
    h("p", null, "Kommunala kulturskolor kan ansluta sig via kommunen. Föreningsdrivna kulturskolor kan ansluta sig om de uppfyller kriterierna för föreningar. Det faktum att en förening får kommunalt stöd gör den inte automatiskt till en kommunal verksamhet."),

    h("h3", null, "Friluftsföreningar och övriga barn- och ungdomsorganisationer"),
    h("p", null, "Friluftsföreningar anslutna till Svenskt Friluftsliv kan ansluta sig. Övriga barn- och ungdomsorganisationer kan ansluta sig om de är medlemmar i en riksorganisation som får bidrag från MUCF."),

    h("h2", null, "Steg för steg: så ansluter ni er förening"),

    h("h3", null, "Steg 1: Kontrollera att ni uppfyller kraven"),
    h("p", null, "Innan ni påbörjar ansökan, bekräfta att:"),
    h("ul", null,
      h("li", null, "Er förening tar emot LOK-stöd från RF (för idrottsföreningar), eller uppfyller något av de alternativa kriterierna"),
      h("li", null, "Ni har ett svenskt organisationsnummer"),
      h("li", null, "Ni har ett bank- eller plusgiro registrerat på föreningen"),
    ),

    h("h3", null, "Steg 2: Förbered dokumenten"),
    h("p", null, "Det går inte att spara en påbörjad ansökan, så ha allt klart innan ni loggar in. Ni behöver:"),
    h("ul", null,
      h("li", null, h("strong", null, "Föreningens organisationsnummer"), ": det namn som är kopplat till organisationsnumret visas för föräldrar i Fritidskortet. Vill ni visa ett annat namn behöver ni anmäla det till Skatteverket (ekonomisk förening: Bolagsverket) innan ansökan."),
      h("li", null, h("strong", null, "Bank- eller plusgironummer"), ": dit betalningarna från Fritidskortet ska gå. Numret måste vara registrerat på er förening."),
      h("li", null, h("strong", null, "Föreningens stadgar"), ": som PDF, JPG, PNG eller TIFF."),
      h("li", null, h("strong", null, "Justerat protokoll från senaste årsmötet."), ""),
      h("li", null, h("strong", null, "Justerat protokoll från det konstituerande styrelsemötet"), " efter senaste årsmötet."),
      h("li", null, "Om det inte är föreningens firmatecknare som ansöker behöver ni även ett styrelsebeslut om att den personen är behörig att söka."),
    ),

    h("h3", null, "Steg 3: Bestäm hur ni ska hantera betalningarna"),
    h("p", null, "Innan ni ansöker behöver ni bestämma om betalningar från Fritidskortet ska:"),
    h("ul", null,
      h("li", null, "Gå direkt till föreningens egna bank- eller plusgiro, eller"),
      h("li", null, "Gå via ett föreningssystem som är anslutet till Fritidskortet"),
    ),
    h("p", null, "Om ert föreningssystem inte är anslutet till Fritidskortet behöver ni ändå ange ert egna bankgiro i ansökan. Tänk också på att informera era medlemmar om vilket OCR-nummer de ska ange vid betalning. Det är så ni kan koppla ihop delbetalningen från Fritidskortet med rätt faktura."),

    h("h3", null, "Steg 4: Skicka in ansökan via Försäkringskassan"),
    h("p", null, "Ansökan till Fritidskortets register är en e-tjänst hos Försäkringskassan. Du loggar in med BankID. Från april 2026 är det även möjligt att ansöka digitalt med skyddad folkbokföring."),
    h("p", null, "Ha dokumenten redo för uppladdning, ansökan kan inte sparas halvvägs."),

    h("h3", null, "Steg 5: Vänta på besked"),
    h("p", null, "Föreningar och kulturskolor kan ansöka om att ansluta sig när som helst. I många fall godkänns ansökan direkt, men det kan ta upp till 30 dagar om kompletteringar behövs. Ni behöver bara ansöka en gång, det krävs inte en ny ansökan varje år."),

    h("h3", null, "Steg 6: Berätta för era medlemmar"),
    h("p", null, "När er förening är ansluten är det viktigt att informera era medlemmar och deras familjer. Använd gärna Fritidskortets logga tillsammans med texten \"Vi är en del av Fritidskortet\" på er webbplats, i sociala medier, på fakturor och i annat tryckt material."),

    h("h2", null, "Hur fungerar betalningen i praktiken?"),
    h("p", null, "När föreningen är ansluten fungerar det så här:"),
    h("ol", null,
      h("li", null, "Barnet anmäls till en ledarledd, regelbunden aktivitet hos er förening"),
      h("li", null, "Barnet får sin plats bekräftad och en faktura med OCR-nummer"),
      h("li", null, "Vårdnadshavaren loggar in på fritidskortet.se, väljer barn och belopp, anger föreningens bankgiro och OCR-nummer"),
      h("li", null, "Pengarna betalas ut till föreningens konto inom tre bankdagar"),
    ),
    h("p", null, "Transaktionen är oftast 550 kronor eller 2 500 kronor, men kan vara vilket belopp som helst mellan 1 krona och 2 500 kronor. Om avgiften är högre än saldot på kortet gör vårdnadshavaren en extra betalning på det återstående beloppet på vanligt sätt."),

    h("h2", null, "Viktigt om sista betalningsdag"),
    h("p", null, "Pengar som inte används senast 30 november varje år kan inte sparas till nästa år. Informera era familjer om detta i god tid under hösten."),

    h("h2", null, "Varför ska er förening ansluta sig?"),
    h("p", null, h("strong", null, "Fler barn kan delta."), " Fritidskortet sänker den ekonomiska tröskeln för familjer som annars kanske inte haft råd med föreningsavgifter. Det kan direkt öka antalet aktiva medlemmar."),
    h("p", null, h("strong", null, "Ni syns i Fritidskortets sökfunktion."), " När föräldrar söker efter aktiviteter för sina barn på fritidskortet.se visas anslutna föreningar. Det är gratis exponering mot en målgrupp som aktivt letar efter aktiviteter."),
    h("p", null, h("strong", null, "Det kostar ingenting."), " Det är helt gratis att ansluta sin förening."),
    h("p", null, h("strong", null, "Administrationen förändras minimalt."), " Föreningar som är med i Fritidskortet behöver inte ändra sina rutiner för betalningar. Avgifter betalas på samma sätt som tidigare. Det enda nya är att föreningen kan få en delbetalning från Fritidskortet som behöver matchas mot rätt faktura via OCR-numret."),

    h("h2", null, "Checklista: innan ni ansöker"),
    h("ul", null,
      h("li", null, "Kontrollera att föreningen tar emot LOK-stöd från RF (eller uppfyller alternativa krav)"),
      h("li", null, "Ta fram organisationsnumret"),
      h("li", null, "Kontrollera att bank- eller plusgiro är registrerat på föreningen"),
      h("li", null, "Spara ner stadgar som PDF/JPG/PNG/TIFF"),
      h("li", null, "Ta fram justerat protokoll från senaste årsmötet"),
      h("li", null, "Ta fram justerat protokoll från konstituerande styrelsemöte"),
      h("li", null, "Bestäm om betalningar ska gå via eget giro eller föreningssystem"),
      h("li", null, "Bestäm vilket OCR-nummer eller meddelande familjer ska ange vid betalning"),
      h("li", null, "Säkerställ att ansökaren har BankID"),
    ),

    h("h2", null, "Sammanfattning"),
    h("p", null, "Fritidskortet är en stor möjlighet för svenska föreningar, inte minst för att nå familjer som tidigare kanske inte haft råd att låta sina barn delta. Från 2026 gäller stödet hela kalenderåret med grundbeloppet 550 kronor per barn, och upp till 2 500 kronor för ekonomiskt utsatta hushåll. Ansökan är gratis, görs en gång och godkänns i de flesta fall direkt. Det viktigaste är att ha dokumenten på plats innan ni loggar in, eftersom ansökan inte kan sparas halvvägs."),
    h("p", null, "Ansök på fritidskortet.se. Ju fler föreningar som ansluter sig, desto fler barn kan delta i organiserade fritidsaktiviteter."),
    h("p", null, h("em", null, "Adminlaget hjälper föreningar att hålla ordning på administrationen, från bokföring till bidragshantering.")),
  ),
};

const startaIdeellForening: Article = {
  slug: "starta-ideell-forening-steg-for-steg",
  title: "Så startar du en ideell förening: steg för steg 2026",
  description:
    "Vill du starta en ideell förening? Här får du en praktisk guide till stadgar, konstituerande möte, organisationsnummer och vanliga fallgropar.",
  excerpt:
    "Allt du behöver veta för att starta en ideell förening i Sverige: stadgar, styrelse, organisationsnummer, bankkonto och vanliga misstag.",
  publishedAt: "2026-09-10",
  updatedAt: "2026-09-10",
  readingMinutes: 10,
  keywords: [
    "starta ideell förening",
    "hur startar man en ideell förening",
    "ideell förening stadgar",
    "organisationsnummer ideell förening",
    "konstituerande möte förening",
  ],
  content: h(Fragment, null,
    h("p", null, "En ideell förening är det vanligaste sättet att organisera ett gemensamt intresse i Sverige. Idrottsklubbar, kulturföreningar, intresseorganisationer och vänskapsföreningar använder nästan alla samma grundform. Det är flexibelt, kräver inget insatskapital och passar både små lokala grupper och riksomfattande organisationer."),
    h("p", null, "I den här guiden går vi igenom vad du behöver tänka på, steg för steg, från första mötet till färdigt organisationsnummer."),

    h("h2", null, "Vad är en ideell förening?"),
    h("p", null, "En ideell förening är en förening som har ett ideellt ändamål. Det betyder att den inte drivs för att ge vinst till ägare eller medlemmar. Eventuella överskott ska gå tillbaka till föreningens verksamhet."),
    h("p", null, "Föreningen bildas när minst tre personer skriver under stadgar och håller ett konstituerande möte. Efter det kan ni ansöka om organisationsnummer hos Skatteverket."),
    h("p", null, "En ideell förening skiljer sig från en ekonomisk förening, som är en affärsmässig form där medlemmarna ekonomiskt deltar i verksamheten. Den skiljer sig också från aktiebolag och enskild firma, som är bolagsformer för näringsverksamhet."),

    h("h2", null, "Vem passar en ideell förening för?"),
    h("p", null, "Formen passar nästan alla som vill organisera ett gemensamt intresse utan vinstsyfte. Vanliga exempel är:"),
    h("ul", null,
      h("li", null, "Idrottsföreningar och motionsklubbar"),
      h("li", null, "Kultur- och musikföreningar"),
      h("li", null, "Intresse- och branschorganisationer"),
      h("li", null, "Vänskaps- och lokalsamfundsföreningar"),
      h("li", null, "Välgörenhets- och hjälporganisationer"),
    ),
    h("p", null, "Har ni ett tydligt syfte, minst tre personer som vill bilda föreningen och en plan för hur den ska styras, är ni redo att gå vidare."),

    h("h2", null, "Steg för steg: så startar du föreningen"),

    h("h3", null, "Steg 1: Formulera syfte och namn"),
    h("p", null, "Börja med att diskutera vad föreningen ska göra och vem den ska finnas till för. Syftet ska vara tydligt nog att skriva in i stadgarna. Ett vagt syfte gör det svårt att senare förklara vad föreningen står för, både för medlemmar och myndigheter."),
    h("p", null, "Välj också ett namn som är unikt och inte lätt förväxlas med befintliga föreningar eller företag. Det är praktiskt att söka på webben och i Bolagsverkets register innan ni bestämmer er."),

    h("h3", null, "Steg 2: Skriv stadgar"),
    h("p", null, "Stadgarna är föreningens regelverk. De ska innehålla vissa obligatoriska punkter för att föreningen ska kunna få ett organisationsnummer. Ni kan utgå från en färdig mall och anpassa den efter er verksamhet."),
    h("p", null, h("strong", null, "Stadgarna ska alltid innehålla:")),
    h("ul", null,
      h("li", null, "Föreningens namn"),
      h("li", null, "Föreningens säte, det vill säga vilken ort föreningen officiellt hör hemma i"),
      h("li", null, "Syfte och verksamhet"),
      h("li", null, "Vem som kan bli medlem och hur medlemskapet upphör"),
      h("li", null, "Hur medlemsavgift beslutas"),
      h("li", null, "Hur årsmötet ska gå till, kallelsetid och beslutsordning"),
      h("li", null, "Hur styrelsen väljs och hur länge den sitter"),
      h("li", null, "Hur stadgar kan ändras"),
      h("li", null, "Hur föreningen kan upplösas och vad som ska hända med tillgångarna"),
    ),
    h("p", null, "Många föreningar lägger också till regler om firmateckning, revisorer, valberedning och etiska riktlinjer. Det är inte obligatoriskt, men det underlättar styrelsearbetet."),

    h("h3", null, "Steg 3: Kalla till konstituerande möte"),
    h("p", null, "Det konstituerande mötet är det första officiella mötet där föreningen bildas. Här antas stadgarna och den första styrelsen väljs. Förbered en dagordning i förväg och skriv tydliga protokoll."),
    h("p", null, "På mötet bör ni åtminstone:"),
    h("ul", null,
      h("li", null, "Fastställa föreningens namn och säte"),
      h("li", null, "Anta stadgarna"),
      h("li", null, "Välja styrelse"),
      h("li", null, "Välja ordförande och andra befattningar"),
      h("li", null, "Bestämma vem som tecknar firma"),
      h("li", null, "Välja revisorer om det behövs"),
    ),
    h("p", null, "Tänk på att protokollet från det konstituerande mötet kan behöva visas upp senare, till exempel vid ansökan om organisationsnummer eller bankkonto."),

    h("h3", null, "Steg 4: Ansök om organisationsnummer hos Skatteverket"),
    h("p", null, "När stadgarna är antagna och styrelsen vald kan ni ansöka om organisationsnummer hos Skatteverket. Det görs enklast via Skatteverkets e-tjänst. Ni behöver vanligtvis uppge föreningens namn, stadgar, protokoll från det konstituerande mötet och kontaktuppgifter till styrelsen."),
    h("p", null, "Organisationsnumret är föreningens identitet i kontakt med myndigheter, banker och andra organisationer. Utan det går det inte att öppna ett föreningskonto eller ansöka om många bidrag."),

    h("h3", null, "Steg 5: Öppna bankkonto"),
    h("p", null, "En ideell förening behöver ett separat bankkonto i föreningens namn. Det är viktigt att föreningens pengar inte blandas med privata medel, varken för revisorernas skull eller för att styrelsen ska kunna följa ekonomin."),
    h("p", null, "Banken kommer att be om organisationsnummer, stadgar och protokoll. Vissa banker erbjuder särskilda föreningskonton med lägre avgifter. Jämför villkoren innan ni väljer."),

    h("h3", null, "Steg 6: Sätt upp medlemsregister och ekonomiska rutiner"),
    h("p", null, "Skapa ett enkelt medlemsregister där ni håller reda på namn, kontaktuppgifter och medlemsavgifter. Tänk också igenom hur ni ska hantera kvitton, fakturor och utbetalningar."),
    h("p", null, "Även om föreningen inte är bokföringsskyldig från start är det klokt att ha ordning på underlagen. Om ni senare växer eller börjar bedriva näringsverksamhet slipper ni bygga upp rutinerna i efterhand."),

    h("h2", null, "När behövs F-skatt, moms och arbetsgivarregistrering?"),
    h("p", null, "Många ideella föreningar behöver inte registrera sig för F-skatt eller moms. Men om föreningen säljer varor eller tjänster, bedriver näringsverksamhet eller har anställd personal, kan registrering behövas."),
    h("p", null, "Skatteverkets webbplats beskriver när en ideell förening ska registreras för moms och arbetsgivarregistrering. Kontrollera alltid aktuella regler där innan ni tar emot avgifter, säljer lotterier eller anställer personal."),

    h("h2", null, "Vad gäller för verklig huvudman?"),
    h("p", null, "Vissa ideella föreningar behöver registrera verklig huvudman hos Bolagsverket. Det gäller bland annat föreningar som bedriver näringsverksamhet eller har tillgångar över en viss nivå. Reglerna kan ändras, så kontrollera alltid vad som gäller för er förening på Bolagsverkets webbplats."),

    h("h2", null, "Vanliga misstag när man startar en förening"),
    h("ul", null,
      h("li", null, h("strong", null, "Otydligt syfte."), " Ett syfte som är för brett eller för vagt gör det svårt att förklara vad föreningen gör."),
      h("li", null, h("strong", null, "Saknade firmateckningsregler."), " Stadgarna ska tydligt ange vem som får teckna firma och hur beslut om ekonomi fattas."),
      h("li", null, h("strong", null, "Blandade pengar."), " Föreningens ekonomi och privata utgifter ska hållas isär från dag ett."),
      h("li", null, h("strong", null, "Dåliga protokoll."), " Protokollen är föreningens minne. Skriv alltid datum, närvarande, beslut och vem som justerat protokollet."),
      h("li", null, h("strong", null, "Glömd årsmötesordning."), " Stadgarna ska beskriva hur årsmötet kallas, hur lång kallelsetiden är och hur omröstningar går till."),
    ),

    h("h2", null, "Vanliga frågor"),

    h("h3", null, "Vad kostar det att starta en ideell förening?"),
    h("p", null, "Det kostar inget att bilda själva föreningen. En eventuell avgift för organisationsnummer, bankkonto eller stadgeregistrering varierar mellan banker och myndigheter. Kolla aktuella priser hos Skatteverket och den bank ni väljer."),

    h("h3", null, "Hur lång tid tar det?"),
    h("p", null, "Själva bildandet kan ske på ett enda möte. Att få organisationsnummer från Skatteverket tar vanligtvis några veckor, ibland längre vid hög belastning. Bankkontot öppnas oftast efter att organisationsnumret är klart."),

    h("h3", null, "Kan personer utan svenskt personnummer sitta i styrelsen?"),
    h("p", null, "Reglerna kan skilja sig åt beroende på föreningens verksamhet och storlek. Kontrollera alltid aktuella krav hos Skatteverket och Bolagsverket."),

    h("h3", null, "Behöver föreningen revisorer?"),
    h("p", null, "Mindre ideella föreningar behöver inte alls ha revisorer. Större föreningar eller sådana med särskilda krav i stadgarna kan behöva det. Läs mer om gränserna på Skatteverkets webbplats."),

    h("h3", null, "Måste föreningen betala skatt på bidrag?"),
    h("p", null, "Ideella föreningar kan ofta ta emot vissa bidrag och gåvor utan att betala skatt, förutsatt att pengarna används för det ideella ändamålet. Reglerna skiljer sig åt beroende på bidragets typ och storlek, så rådfråga Skatteverket eller en revisor om ni är osäkra."),

    h("h3", null, "Behöver föreningen ett svenskt bankkonto?"),
    h("p", null, "Ja, i praktiken behöver nästan alla föreningar ett svenskt bankkonto för att kunna ta emot medlemsavgifter, bidrag och andra inbetalningar. Det gör också bokföring och revision betydligt enklare."),

    h("h2", null, "Sammanfattning"),
    h("p", null, "Att starta en ideell förening handlar om tre saker: ett tydligt syfte, välskrivna stadgar och en styrelse som tar ansvar. Resten är praktiska steg: konstituerande möte, organisationsnummer, bankkonto och ordnade rutiner."),
    h("p", null, "Behöver ni hjälp med stadgar, bokföring eller löpande administration? Adminlaget arbetar med föreningar i hela Sverige och hjälper er att få ordning på ekonomin från start."),
    h("p", null, h("em", null, "Källa och vidare läsning: Skatteverket och Bolagsverket."))
  ),
};

const KALLOR: Record<number, string> = {
  1: "https://www.skatteverket.se/foreningar/deklarera/deklareraochbetalaskattienideellforeningelleretttrossamfund.4.1927c51b15e7ee438721ff8.html",
  2: "https://www.skatteverket.se/foreningar/deklarera/safyllerduiinkomstdeklaration3.4.1927c51b15e7ee43872276b.html",
  3: "https://www.skatteverket.se/foretag/etjansterochblanketter/blanketterbroschyrer/blanketter/info/2501.4.39f16f103821c58f680006532.html",
  4: "https://www4.skatteverket.se/rattsligvagledning/324999.html",
};

const ref = (n: number) =>
  h("a", { href: KALLOR[n], target: "_blank", rel: "noopener noreferrer" }, `[${n}]`);

const ext = (href: string, text: string) =>
  h("a", { href, target: "_blank", rel: "noopener noreferrer" }, text);

const deklareraIdeellForening: Article = {
  slug: "deklarera-ideell-forening-steg-for-steg",
  title: "Så deklarerar du en ideell förening: guide 2026",
  description:
    "Måste en ideell förening deklarera? Här får du svaret: deklarationsplikt, befrielse, blanketter, datum och vanliga misstag. Praktisk guide 2026.",
  excerpt:
    "Allt om deklaration för ideella föreningar: när ni måste deklarera, när ni slipper, vilka blanketter som gäller och vanliga misstag att undvika.",
  publishedAt: "2026-09-30",
  updatedAt: "2026-09-30",
  readingMinutes: 8,
  keywords: [
    "deklarera ideell förening",
    "inkomstdeklaration 3 förening",
    "när ska ideell förening deklarera",
    "befrielse från deklarationsplikt förening",
    "särskild uppgift förening",
  ],
  content: h(Fragment, null,
    h("p", null, "Deklarationen blir lättare att hantera när bokslutet är klart och ni vet vilka uppgifter föreningen ska lämna. Börja med att skilja mellan skattepliktiga inkomster, skattefria inkomster och skyldigheten att lämna uppgifter till Skatteverket. Även en förening som inte betalar inkomstskatt kan behöva lämna uppgifter."),
    h("p", null, "Här går vi igenom blanketterna, datumen och möjligheten att ansöka om befrielse från särskild uppgift. Guiden är uppdaterad den 30 september 2026. Deklarationen som lämnas under 2026 avser normalt ett tidigare avslutat räkenskapsår."),

    h("h2", null, "Måste en ideell förening deklarera?"),
    h("p", null, "Ideella föreningar använder Inkomstdeklaration 3. Föreningen ska alltid lämna deklarationen om den ska redovisa underlag för inkomstskatt, fastighetsskatt, fastighetsavgift, särskild löneskatt eller avkastningsskatt. ", ref(1)),
    h("p", null, "En allmännyttig förening ska normalt redovisa sin skattefria verksamhet i bilagan Särskild uppgift. Har föreningen bara skattefri verksamhet räcker vanligtvis denna bilaga tillsammans med deklarationens första sida, om föreningen inte har beviljats befrielse. Vid digital inlämning följer ni e-tjänstens steg för att lämna och underteckna uppgifterna. ", ref(1)),
    h("p", null, "Använd ", ext("https://www.skatteverket.se/foreningar/deklarera/deklarationsguideforideellforeningstiftelseochtrossamfund.4.7da1d2e118be03f8e4f401d.html", "Skatteverkets deklarationsguide"), " för att avgöra vad just er förening ska lämna. Utgå inte enbart från att föreningen är liten eller saknar överskott."),

    h("h2", null, "Vilka blanketter gäller?"),
    h("p", null, "Huvudblanketten är Inkomstdeklaration 3, SKV 2003. Följande bilagor kan vara aktuella:"),
    h("table", null,
      h("thead", null, h("tr", null, h("th", null, "Bilaga"), h("th", null, "Användning"))),
      h("tbody", null,
        h("tr", null, h("td", null, "Kapitalinkomster m.m., INK3K"), h("td", null, "Skattepliktiga kapitalinkomster, till exempel ränta och utdelning i en förening som inte är allmännyttig, och tillhörande avdrag")),
        h("tr", null, h("td", null, "Särskild uppgift, INK3SUF"), h("td", null, "Uppgifter om allmännyttiga föreningars och registrerade trossamfunds skattefria verksamhet")),
        h("tr", null, h("td", null, "Räkenskapsschema, INK3R"), h("td", null, "Räkenskapsuppgifter")),
        h("tr", null, h("td", null, "Skattemässiga justeringar, INK3S"), h("td", null, "Justeringar mellan bokfört och skattemässigt resultat")),
      ),
    ),
    h("p", null, "Ni ska bara lämna de bilagor som gäller för föreningen. INK3SUS är särskild uppgift för stiftelser och ska inte förväxlas med föreningens INK3SUF. ", ref(1), " ", ref(2)),

    h("h2", null, "När ska deklarationen vara inne?"),
    h("p", null, "För kalenderåret 2025 var sista deklarationsdag 3 augusti 2026. För kalenderåret 2026 är sista dagen 2 augusti 2027. ", ref(1)),
    h("p", null, "För räkenskapsår som avslutas under 2026 gäller:"),
    h("table", null,
      h("thead", null, h("tr", null, h("th", null, "Räkenskapsåret avslutas"), h("th", null, "Sista deklarationsdag"))),
      h("tbody", null,
        h("tr", null, h("td", null, "Januari–april 2026"), h("td", null, "1 december 2026")),
        h("tr", null, h("td", null, "Maj–juni 2026"), h("td", null, "15 januari 2027")),
        h("tr", null, h("td", null, "Juli–augusti 2026"), h("td", null, "1 april 2027")),
        h("tr", null, h("td", null, "September–december 2026"), h("td", null, "2 augusti 2027")),
      ),
    ),
    h("p", null, "Datumen gäller oavsett om ni lämnar digitalt eller på papper. Kontrollera föreningens datum och eventuellt beviljat anstånd innan ni planerar arbetet. ", ref(1)),
    h("p", null, h("strong", null, "Tips:"), " Lägg in både sista deklarationsdag och en intern deadline för underlag, granskning och underskrift."),

    h("h2", null, "Vilka inkomster är skattepliktiga?"),
    h("p", null, "Ideella föreningar kan vara skattskyldiga för kapital-, fastighets- och rörelseinkomster. Skatten är 20,6 procent av den beskattningsbara inkomsten. ", ref(1)),
    h("p", null, "Inkomster från den ideella medlemsverksamheten, som medlemsavgifter, bidrag och gåvor, är skattefria för alla ideella föreningar, oavsett om de är allmännyttiga eller inte. Håll isär medlemsavgifter och andra avgifter som medlemmarna betalar, till exempel träningsavgifter. Bidrag som ska täcka kostnader i en skattepliktig verksamhet är skattepliktiga. ", ref(1)),
    h("p", null, "För allmännyttiga föreningar finns ytterligare undantag. Allmännyttighet förutsätter att ändamåls-, verksamhets-, fullföljds- och öppenhetskraven är uppfyllda. Kapitalinkomster, som räntor, utdelningar och försäljning av värdepapper och fastigheter, är då alltid skattefria. Även vissa fastighets- och rörelseinkomster kan vara skattefria. ", ref(1), " ", ref(4)),
    h("p", null, "Rörelseinkomster bedöms samlat. En verksamhet som bedrivs stadigvarande med anställd personal och utan naturligt samband med den allmännyttiga verksamheten är alltid skattepliktig, till exempel ett café. Övriga rörelseinkomster blir skattefria om minst 75 procent av dem har naturligt samband med verksamheten eller kommer från hävdvunna finansieringskällor. Exempel på sådana inkomster är träningsavgifter, entréintäkter, enklare fika vid föreningens evenemang, lotterier och reklam på föreningskläder. Kommer mer än 25 procent från annat, som uppdrag åt företag eller kommuner, blir alla rörelseinkomster skattepliktiga. ", ref(1)),
    h("p", null, "Skilj därför på verksamheterna redan i bokföringen och dokumentera hur gemensamma kostnader fördelas. Det underlättar bedömningen av vilka intäkter som ska beskattas och vilka kostnader som hör till dem."),

    h("h2", null, "Vad är särskild uppgift?"),
    h("p", null, "I INK3SUF lämnar föreningen uppgifter om sitt ändamål och den skattefria verksamhetens intäkter och kostnader. Bilagan innehåller också frågor om fastigheter."),
    h("p", null, "Här redovisas bland annat medlemsavgifter, bidrag och gåvor. Att inkomsten är skattefri innebär alltså inte att den ska utelämnas. Beloppen från denna bilaga förs inte över som skattepliktig inkomst till deklarationens första sida. ", ref(2)),

    h("h2", null, "När kan föreningen ansöka om befrielse?"),
    h("p", null, "En allmännyttig förening som bara har skattefria inkomster kan ansöka om befrielse från särskild uppgift på blankett SKV 2501. Om Skatteverket godkänner ansökan skickas ingen deklaration ut under fem år. Spara beslutet och notera vilka år det gäller. ", ref(3)),
    h("p", null, "Befrielse beviljas inte om föreningen har skattepliktig näringsverksamhet, har tryggat pension till anställda eller har en fastighet som den ska betala fastighetsskatt eller fastighetsavgift för. Nybildade föreningar får i regel inte befrielse. ", ref(3)),
    h("p", null, "Befrielsen gäller särskild uppgift. Den undanröjer inte en deklarationsskyldighet som uppkommer på grund av skattepliktiga inkomster eller andra skatteunderlag. Följ därför upp om verksamheten förändras och kontrollera vad föreningen då behöver lämna. ", ref(1), " ", ref(3)),
    h("p", null, "En inskickad ansökan är inte ett beslut om befrielse. Planera för att lämna uppgifterna i tid om befrielse inte har beviljats. Kontrollera i god tid om ni behöver ansöka på nytt när perioden löper ut."),

    h("h2", null, "Så lämnar ni in deklarationen"),
    h("p", null, "Hela deklarationen kan lämnas digitalt i e-tjänsten Inkomstdeklaration 2, 3 och 4 eller via ett deklarationsprogram, om ni har rätt behörighet. Använder ni i stället tjänsten Filöverföring för SRU-bilagor ska första sidan lämnas på papper och undertecknas av behörig firmatecknare. ", ref(1)),
    h("p", null, "Den särskilda uppgiften får bara skrivas under digitalt av en person som är registrerad som ensam firmatecknare hos Bolagsverket eller Skatteverket. Ett deklarationsombud får inte skriva under den. Titeln kassör eller ordförande räcker inte i sig. Registrering hos Skatteverket görs på blankett SKV 4801, med underlag som visar vem som är firmatecknare, till exempel ett protokoll. ", ref(1)),
    h("p", null, "Spara kvittensen efter digital inlämning. Vid pappersinlämning bör ni spara en kopia av det som skickas in."),

    h("h2", null, "Vanliga misstag att undvika"),
    h("ul", null,
      h("li", null, h("strong", null, "Att likställa skattefrihet med befrielse från uppgiftslämnande."), " Kontrollera båda frågorna separat."),
      h("li", null, h("strong", null, "Att använda fel års datum."), " Koppla påminnelsen till föreningens faktiska bokslutsdatum."),
      h("li", null, h("strong", null, "Att upptäcka behörighetsproblem sista dagen."), " Registrering av firmatecknare tar tid, så bestäm tidigt vem som fyller i, granskar och skriver under."),
      h("li", null, h("strong", null, "Att behandla bokfört resultat som färdigt skatteunderlag."), " Kontrollera om skattemässiga justeringar behövs."),
      h("li", null, h("strong", null, "Att sakna dokumentation."), " Spara bokslut, beräkningar, inlämnade uppgifter och beslut tillsammans."),
    ),

    h("h2", null, "Vanliga frågor"),

    h("h3", null, "Måste en förening utan inkomster lämna uppgifter?"),
    h("p", null, "Avsaknad av inkomster innebär inte automatiskt befrielse. En allmännyttig förening ska normalt lämna särskild uppgift tills den har beviljats befrielse. Använd deklarationsguiden för föreningens situation. ", ref(1), " ", ref(3)),

    h("h3", null, "Ska tomma beloppsfält fyllas med nollor?"),
    h("p", null, "Nej. Skatteverkets instruktion är att lämna fält utan belopp tomma. ", ref(2)),

    h("h3", null, "Vad händer om deklarationen blir sen?"),
    h("p", null, "Föreningen kan få betala förseningsavgift. En avgift är 1 250 kronor, och beroende på hur sent deklarationen kommer in kan upp till tre avgifter tas ut, totalt 3 750 kronor. Skatteverket skickar först ett förslag till beslut som ni kan svara på. Lämna uppgifterna så snart som möjligt. Att kontakta myndigheten innebär inte i sig att ni har fått anstånd eller befrielse från avgift. ", ref(1)),

    h("h3", null, "Vad ska nästa kassör få vid överlämningen?"),
    h("p", null, "Lämna över bokslut, deklarationsunderlag, kvittenser, skattebesked och eventuellt beslut om befrielse. Dokumentera också vem som har digital behörighet och vilka datum styrelsen behöver bevaka."),

    h("p", null, "När deklarationen är inlämnad återstår att följa upp slutskattebeskedet och eventuell betalning. Lägg in även detta i föreningens ekonomirutiner."),
    h("p", null, h("strong", null, "Behöver föreningen hjälp med bokföring och deklaration?"), " Kontakta ", h("a", { href: "/#kontakt" }, "Adminlaget"), " för att diskutera ett upplägg som passar er verksamhet."),

    h("h2", null, "Källor"),
    h("ol", null,
      h("li", null, "Skatteverket: ", ext("https://www.skatteverket.se/foreningar/deklarera/deklareraochbetalaskattienideellforeningelleretttrossamfund.4.1927c51b15e7ee438721ff8.html", "Deklarera och betala skatt i en ideell förening eller ett trossamfund")),
      h("li", null, "Skatteverket: ", ext("https://www.skatteverket.se/foreningar/deklarera/safyllerduiinkomstdeklaration3.4.1927c51b15e7ee43872276b.html", "Så fyller du i Inkomstdeklaration 3")),
      h("li", null, "Skatteverket: ", ext("https://www.skatteverket.se/foretag/etjansterochblanketter/blanketterbroschyrer/blanketter/info/2501.4.39f16f103821c58f680006532.html", "Ansökan om befrielse från att lämna särskild uppgift, SKV 2501")),
      h("li", null, "Skatteverket, Rättslig vägledning: ", ext("https://www4.skatteverket.se/rattsligvagledning/324999.html", "Villkor för skattebefrielse")),
    ),
  ),
};

const fakturahanteringForening: Article = {
  slug: "fakturahantering-ideell-forening",
  title: "Fakturahantering i ideella föreningar: så automatiserar ni flödet",
  description:
    "Så får föreningen ordning på leverantörsfakturor: attest, betalning och bokföring. Lär dig automatisera flödet med Mimrix och Fortnox via API.",
  excerpt:
    "Fakturor som ligger i någons mejl, attest som dröjer och dubbelregistrering i bokföringen. Så effektiviserar föreningen fakturaflödet, till exempel med Mimrix kopplat till Fortnox.",
  publishedAt: "2026-10-09",
  updatedAt: "2026-10-09",
  readingMinutes: 6,
  keywords: [
    "fakturahantering förening",
    "leverantörsfakturor förening",
    "attestera fakturor förening",
    "automatisera fakturahantering",
    "Fortnox förening",
    "Mimrix",
  ],
  content: h(Fragment, null,
    h("p", null, "I många föreningar kommer fakturorna in på flera håll samtidigt. Några skickas till kassörens privata mejl, några till en tränare och några kommer fortfarande på papper till klubbstugan. Sedan ska någon godkänna dem, någon betala dem och någon bokföra dem. När ingen har överblick blir det lätt påminnelseavgifter, missade underlag och mycket handpåläggning."),
    h("p", null, "Den här guiden går igenom hur ett bra fakturaflöde ser ut i en ideell förening och hur ni kan automatisera stora delar av det, till exempel med tjänsten Mimrix som kopplas till Fortnox via API."),

    h("h2", null, "Vad ingår i fakturahantering?"),
    h("p", null, "Med fakturahantering menas oftast hanteringen av leverantörsfakturor, alltså de fakturor föreningen själv ska betala. Flödet består i regel av fem steg:"),
    h("ol", null,
      h("li", null, h("strong", null, "Mottagning: "), "fakturan kommer in som pdf, e-faktura eller papper."),
      h("li", null, h("strong", null, "Registrering: "), "leverantör, belopp, moms, förfallodatum och OCR förs in."),
      h("li", null, h("strong", null, "Kontering: "), "fakturan kopplas till rätt konto och gärna till rätt sektion, lag eller projekt."),
      h("li", null, h("strong", null, "Attest: "), "en behörig person godkänner att kostnaden är riktig."),
      h("li", null, h("strong", null, "Betalning och bokföring: "), "fakturan betalas i tid och bokförs med underlaget sparat."),
    ),

    h("h2", null, "Vanliga problem i föreningar"),
    h("ul", null,
      h("li", null, "Fakturor skickas till fel person och blir liggande tills en påminnelse kommer."),
      h("li", null, "Attesten sker via sms eller muntligt, vilket är svårt att visa för revisorn i efterhand."),
      h("li", null, "Kassören skriver in samma uppgifter både i banken och i bokföringsprogrammet."),
      h("li", null, "Underlagen sparas i olika mejlkorgar och försvinner när en ideell funktionär slutar."),
      h("li", null, "Det är svårt att se vad varje sektion eller lag har kostat under året."),
    ),
    h("p", null, "Ingen av dessa problem beror på slarv. De beror på att ideellt engagerade personer har begränsad tid och att flödet sällan är uttänkt från början."),

    h("h2", null, "Börja med tydliga rutiner"),
    h("p", null, "Innan ni inför något verktyg lönar det sig att bestämma några grundregler i styrelsen:"),
    h("ul", null,
      h("li", null, h("strong", null, "En fakturaadress. "), "Be alla leverantörer skicka fakturor till en och samma adress, helst en funktionsadress som inte är knuten till en person."),
      h("li", null, h("strong", null, "En attestordning. "), "Bestäm vem som får godkänna vilka kostnader och upp till vilket belopp. Skriv in det i styrelseprotokollet."),
      h("li", null, h("strong", null, "Dualitet. "), "Den som betalar ska inte vara samma person som ensam godkänner sin egen kostnad."),
      h("li", null, h("strong", null, "Fasta betaldagar. "), "Betala till exempel två gånger i månaden så att ingen behöver logga in i banken varje dag."),
    ),
    h("p", null, "Tänk också på att bokföringslagen kräver att verifikationer sparas i sju år. Har ni ett digitalt flöde är det betydligt enklare att leva upp till. Läs mer i vår guide om att ", h("a", { href: "/artiklar/bokfora-ideell-forening-steg-for-steg" }, "bokföra en ideell förening"), "."),

    h("h2", null, "Så kan flödet automatiseras"),
    h("p", null, "Ett automatiserat fakturaflöde tar bort de manuella stegen mellan att fakturan kommer in och att den är bokförd. Grundtanken är enkel: uppgifterna ska bara behöva registreras en gång, och systemen ska prata med varandra i stället för att kassören flyttar uppgifter för hand."),
    h("p", null, "I praktiken innebär det att:"),
    h("ul", null,
      h("li", null, "fakturor samlas in automatiskt från en gemensam inkorg eller som e-faktura,"),
      h("li", null, "uppgifter som belopp, moms och förfallodatum läses av från fakturan,"),
      h("li", null, "rätt person får en notis om att attestera, ofta direkt i mobilen,"),
      h("li", null, "attesterade fakturor förs över till bokföringsprogrammet utan dubbelregistrering."),
    ),

    h("h2", null, "Exempel: Mimrix kopplat till Fortnox"),
    h("p", null, "Fortnox är ett av de vanligaste bokföringsprogrammen bland svenska föreningar och har ett öppet API som gör att andra tjänster kan kopplas på. ", ext("https://www.mimrix.com", "Mimrix"), " är ett exempel på en sådan tjänst. Det är en AI-baserad ekonomitjänst som kopplas till Fortnox och arbetar ovanpå den bokföring ni redan har. Fortnox är kvar som ert bokföringssystem, så ni behöver inte flytta något."),
    h("p", null, "I Mimrix sköts leverantörsfakturorna av en AI-agent. Den läser fakturan, föreslår kontering och lägger fram ett förslag till bokföring. Ingenting bokförs utan att en människa har godkänt det, och varje steg loggas så att det går att följa i efterhand. Tjänsten kan även kopplas till Gmail, Slack och banken."),
    h("p", null, "Ett flöde med Mimrix och Fortnox kan se ut så här:"),
    h("ol", null,
      h("li", null, "Föreningen vidarebefordrar sin fakturainkorg till en egen Mimrix-adress, eller laddar upp pdf-filerna direkt."),
      h("li", null, "AI-agenten läser fakturan och föreslår konto enligt BAS-kontoplanen."),
      h("li", null, "Kassören eller annan behörig person granskar och godkänner förslaget."),
      h("li", null, "Den godkända posten bokförs direkt i Fortnox."),
      h("li", null, "Underlag och logg sparas, vilket gör det enkelt för revisorn att granska."),
    ),
    h("p", null, "Bokföring och dokument lagras i Stockholm, underlagen sparas i sju år enligt bokföringslagen och tjänsten hanterar strukturerade e-fakturor enligt standarden EN 16931. Ni kommer igång på några dagar utan IT-projekt. Ideella föreningar får dessutom rabatt på systemet."),
    h("p", null, "Vilka funktioner som ingår och vad det kostar varierar mellan tjänster och abonnemang. Kontrollera alltid aktuella villkor hos leverantören innan ni väljer lösning. Vill ni jämföra program kan ni läsa vår genomgång av ", h("a", { href: "/artiklar/bokforingsprogram-forening-gratis-betalda" }, "bokföringsprogram för föreningar"), "."),

    h("h2", null, "Vad vinner föreningen på det?"),
    h("ul", null,
      h("li", null, h("strong", null, "Tid. "), "Kassören slipper skriva in samma uppgifter flera gånger."),
      h("li", null, h("strong", null, "Färre påminnelseavgifter. "), "Förfallodatum syns tydligt och fakturor blir inte liggande."),
      h("li", null, h("strong", null, "Bättre kontroll. "), "Attesten sparas digitalt och kan visas för revisorn."),
      h("li", null, h("strong", null, "Mindre sårbarhet. "), "Allt ligger i föreningens system, inte i en enskild persons mejl. Det gör även ett ", h("a", { href: "/artiklar/kassorsbyte-forening-overlamning" }, "kassörsbyte"), " mycket enklare."),
      h("li", null, h("strong", null, "Bättre uppföljning. "), "Med kostnadsställen per sektion eller lag ser styrelsen var pengarna går."),
    ),

    h("h2", null, "Så kommer ni igång"),
    h("ol", null,
      h("li", null, "Kartlägg hur fakturorna kommer in i dag och vem som hanterar dem."),
      h("li", null, "Besluta om attestordning och fakturaadress i styrelsen."),
      h("li", null, "Välj bokföringsprogram och eventuell tilläggstjänst för fakturaflödet."),
      h("li", null, "Meddela leverantörerna den nya fakturaadressen."),
      h("li", null, "Testa flödet under en månad och justera sedan."),
    ),

    h("h2", null, "Vanliga frågor"),
    h("h3", null, "Måste en liten förening ha ett digitalt fakturaflöde?"),
    h("p", null, "Nej, det finns inget krav. Men även en liten förening med ett tiotal fakturor i månaden sparar tid och minskar risken för fel med ett enkelt digitalt flöde."),
    h("h3", null, "Vem ska attestera fakturor i en förening?"),
    h("p", null, "Det bestämmer styrelsen. Vanligt är att den som ansvarar för verksamheten där kostnaden uppstått attesterar, och att kassören betalar. Beslutet bör protokollföras."),
    h("h3", null, "Vad är en API-koppling?"),
    h("p", null, "Ett API är ett gränssnitt som låter två system utbyta information automatiskt. En API-koppling mellan en fakturatjänst och Fortnox gör att uppgifterna förs över utan att någon behöver exportera och importera filer."),
    h("h3", null, "Hur länge ska fakturorna sparas?"),
    h("p", null, "En bokföringsskyldig förening ska spara räkenskapsinformation i sju år. Digitala underlag får sparas digitalt."),

    h("h2", null, "Vill ni slippa fakturahanteringen helt?"),
    h("p", null, "Adminlaget hjälper föreningar att sätta upp ett effektivt fakturaflöde i Fortnox eller det system ni vill använda, och kan även sköta attestpåminnelser, betalningar och bokföring åt er. Läs mer om vår ", h("a", { href: "/tjanster/bokforing-forening" }, "bokföring för föreningar"), " eller ", h("a", { href: "/#kontakt" }, "kontakta oss"), " så berättar vi mer."),
  ),
};

export const articles: Article[] = [fakturahanteringForening, deklareraIdeellForening, startaIdeellForening, fritidskortetForening, bidragIdrottsforening, bokforingsprogramForening, arsredovisningIdeellForening, kassorsbyteForening, bokforaIdeellForening];

export const getArticleBySlug = (slug: string) =>
  articles.find((a) => a.slug === slug);

export const getSortedArticles = () =>
  [...articles].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
